/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from 'framework/di';

import { DomHelper } from '../../helpers/dom.helper';
import { requestIdleCallback } from '../../utils';
import { AppService } from '../app/app.service';
import {
  COLLAPSE_STATE,
  COLLECTION_ALL_STORAGE_KEY,
  FILTER_DESKTOP_WRAPPER_SELECTOR,
  FILTER_MOBILE_BUTTON_BACK,
  FILTER_MOBILE_BUTTON_BACK_CLASS,
  FILTER_MOBILE_BUTTON_CLOSE,
  FILTER_MOBILE_BUTTON_HIDDEN,
  FILTER_MOBILE_FULL_HEIGHT_CLASS,
  FILTER_MOBILE_FULL_HEIGHT_SELECTOR,
  FILTER_MOBILE_WRAPPER_SELECTOR,
  FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS,
  FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS,
  FILTER_OPTION_SEARCH_OPTION_SELECTOR,
  FILTER_OPTION_SELECTOR,
  FILTER_OPTION_TITLE_OPENING_CLASS,
  FILTER_TAG_KEY,
  FILTER_TREE_MOBILE_BUTTON_CLEAR,
  FILTER_TREE_MOBILE_TOOL_BAR,
  INITIAL_SCALE,
  LIMIT,
  LIMIT_SETTING,
  PARALLAX_SPEED,
  SHOW_MORE_TYPES,
  SWATCH_PREFIX,
} from './filter.constants';
// import { FilterEvent } from './filter.event';
import { FilterHelper } from './filter.helper';
import { EVENT_NAMES, RangeSliderContext } from './filter.model';
import { FilterService } from './filter.service';
import { FilterStore } from './filter.store';
import { FilterTranslationService } from './filter.translation.service';
import { FilterValidationHelper } from './filter.validation.helper';

/**
 * Rendering service for filter widget UI components.
 *
 * Manages all DOM rendering operations for the filter widget, including filter options,
 * refine-by tags, view more buttons, collection headers, and mobile/desktop layouts.
 * Coordinates with FilterStore for state management and FilterHelper for utilities.
 *
 * @remarks
 * This service handles complex rendering logic including:
 * - Filter option rendering (list, box, swatch, rating, multi-level)
 * - Dynamic "View More" state management
 * - Mobile vs desktop layout differences
 * - Collapse/expand state persistence
 * - Filter tree lazy loading
 * - Collection header rendering
 *
 * Technical Support teams can extend this service to customize rendering behavior,
 * add shop-specific UI elements, or modify filter display logic.
 *
 * @example
 * Extend to customize filter option rendering:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
 *   return class CustomFilterRender extends FilterRender {
 *     async renderOptionList(params) {
 *       // Add custom badge to filter options
 *       await super.renderOptionList(params);
 *       const container = params.dom.querySelector('.boost-sd__filter-option-list');
 *       if (container) {
 *         container.insertAdjacentHTML('afterbegin', '<div class="custom-badge">New</div>');
 *       }
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override collection header rendering:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
 *   return class extends FilterRender {
 *     async renderCollectionHeader() {
 *       await super.renderCollectionHeader();
 *       // Add custom collection description
 *       const header = document.querySelector('#boost-sd__collection-header');
 *       if (header) {
 *         header.insertAdjacentHTML('beforeend', this.getCustomDescription());
 *       }
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterRender {
  /**
   * Tracks drag-listener cleanup functions per filter option.
   * Keyed by filterOptionId (stable across DOM swaps) so that document-level
   * mouseup/touchend listeners are always removed before re-registering, even
   * when the slider Element itself is replaced by lazyLoadFilterTree.
   */
  protected readonly sliderCleanupMap = new Map<string, () => void>();

  /**
   * Creates a new FilterRender instance.
   *
   * @param appService - Application service for accessing app configuration
   * @param filterStore - Filter state store for accessing widget state
   * @param filterHelper - Helper service for filter utilities
   * @param domHelper - DOM manipulation utilities
   * @param filterService - Filter service for business logic
   * @param filterTranslation - Translation service for i18n
   * @param filterValidation - Validation helper for data validation
   */
  constructor(
    private appService: AppService,
    private filterStore: FilterStore,
    private filterHelper: FilterHelper,
    private domHelper: DomHelper,
    private filterService: FilterService,
    private filterTranslation: FilterTranslationService,
    private filterValidation: FilterValidationHelper
  ) {}

  /**
   * Removes the filter option DOM element when it has no values.
   *
   * Removes both desktop and mobile DOM nodes for a filter option when its
   * `values` array is empty, preventing empty filter sections from displaying.
   *
   * @param rawAction - The filter option toggle ID (e.g. `"pf_st_color-toggle"`)
   * @param option - Filter option configuration containing a `values` array
   *
   * @example
   * Override to add custom empty-state handling:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
   *   return class extends FilterRender {
   *     removeDomFilterOptionIfEmpty(rawAction, option) {
   *       super.removeDomFilterOptionIfEmpty(rawAction, option);
   *       if (!option.values?.length) {
   *         console.info('Empty filter removed:', option.filterOptionId);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  removeDomFilterOptionIfEmpty(rawAction: string, option: any) {
    // build desktop
    const document = this.filterStore.state.value.document as HTMLElement;

    if (Array.isArray(option.values) && option.values?.length === 0) {
      // remove dome filter option empty
      document
        .querySelector(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}`)
        ?.closest('.boost-sd__filter-option')
        ?.remove();

      document
        .querySelector(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}-target`)
        ?.closest('.boost-sd__filter-option')
        ?.remove();
    }
  }

  /**
   * Sets the `data-filterOptionId` attribute on filter option DOM elements.
   *
   * Updates both desktop and mobile DOM containers for a filter option with
   * the `filterOptionId` from the option configuration, used for event routing.
   *
   * @param rawAction - The filter option toggle ID
   * @param option - Filter option configuration with a `filterOptionId` property
   */
  buildAttributeDataFilterOptionListItem(rawAction: string, option: any) {
    const document = this.filterStore.state.value.document as HTMLElement;

    // build desktop
    document
      .querySelector(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}`)
      ?.closest('.boost-sd__filter-option')
      ?.setAttribute('data-filterOptionId', option.filterOptionId);

    // build mobile
    document
      .querySelector(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}-target`)
      ?.closest('.boost-sd__filter-option')
      ?.setAttribute('data-filterOptionId', option.filterOptionId);
  }

  /**
   * Sets `data-metadata` attributes on collapse toggle elements.
   *
   * Attaches collapse/expand metadata to the toggle button and its SVG child
   * elements for both desktop and mobile layouts, enabling event delegation.
   *
   * @param rawAction - The filter option toggle ID
   * @param metadata - JSON-serialized action metadata string
   */
  buildEventFilterOptionCollapsed(rawAction: string, metadata: string) {
    const document = this.filterStore.state.value.document as HTMLElement;

    // build desktop
    document
      .querySelector(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}`)
      ?.setAttribute('data-metadata', metadata);
    document
      .querySelector(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction} svg`)
      ?.setAttribute('data-metadata', metadata);
    document
      .querySelector(`${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction} svg path`)
      ?.setAttribute('data-metadata', metadata);

    // build mobile
    document
      .querySelector(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}`)
      ?.setAttribute('data-metadata', metadata);
    document
      .querySelector(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction} svg`)
      ?.setAttribute('data-metadata', metadata);
    document
      .querySelector(`${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction} svg path`)
      ?.setAttribute('data-metadata', metadata);
  }

  /**
   * Shows or hides the search box for a filter option based on platform settings.
   *
   * Removes the search input DOM node for desktop when `showSearchBoxFilterPC`
   * is `false`, or for mobile when `showSearchBoxFilterMobile` is `false`.
   *
   * @param rawAction - The filter option toggle ID
   * @param option - Filter option configuration with `showSearchBoxFilterPC`
   * and `showSearchBoxFilterMobile` boolean flags
   */
  showHideSearchBoxOption(rawAction: string, option: any) {
    // build Show Hide search option
    if (option.showSearchBoxFilterPC || option.showSearchBoxFilterMobile) {
      // if PC not show -> mobile show -> disabled search box desktop
      if (!option.showSearchBoxFilterPC) {
        // Remove dom input desktop
        document
          .querySelector(
            `${FILTER_DESKTOP_WRAPPER_SELECTOR} #${rawAction}-target ${FILTER_OPTION_SEARCH_OPTION_SELECTOR}`
          )
          ?.remove();
      }

      // if Mobile not show -> desktop show -> disabled search box mobile
      if (!option.showSearchBoxFilterMobile) {
        // Remove dom input mobile
        document
          .querySelector(
            `${FILTER_MOBILE_WRAPPER_SELECTOR} #${rawAction}-target ${FILTER_OPTION_SEARCH_OPTION_SELECTOR}`
          )
          ?.remove();
      }
    }
  }

  /**
   * Applies the current collapse state to all filter option DOM elements.
   *
   * Iterates over stored `collapseState` entries and adds or removes CSS classes
   * on target elements to keep the DOM in sync with the current state.
   *
   * @example
   * Override to add custom collapse animations:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
   *   return class extends FilterRender {
   *     handleCollapsedChange() {
   *       super.handleCollapsedChange();
   *       document.querySelectorAll('.boost-sd__filter-option-itemwrapper')
   *         .forEach(el => el.classList.add('animate-collapse'));
   *     }
   *   };
   * });
   * ```
   */
  handleCollapsedChange() {
    const _getCollapseState = this.filterStore.state.value.collapseState;
    const keys = Object.keys(_getCollapseState);
    const parent = this.filterService.getFilterTreeParent();
    if (keys.length > 0) {
      keys.forEach((key) => {
        const action = _getCollapseState[key];
        const targets = action.targets;
        if (targets.length > 0) {
          targets.forEach((target: any) => {
            const targetElement = parent?.querySelector(`#${target.targetId}`);
            const shouldAddClass = target.shouldAddClass;

            if (targetElement) {
              shouldAddClass
                ? targetElement.classList.add(target.classToggle)
                : targetElement.classList.remove(target.classToggle);
            }
          });
        }
      });
    }
  }

  /**
   * Initializes collapse/expand state for all active filter options.
   *
   * Computes and sets the initial `collapseState` for every active filter option
   * based on `isCollapsePC`/`isCollapseMobile` settings, layout, and any persisted
   * toggle state when `keepToggleState` is enabled. Also registers Escape key
   * support to close the currently focused filter option.
   *
   * @example
   * Override to force all filters open on initialization:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
   *   return class extends FilterRender {
   *     initFilterOptionCollapse() {
   *       super.initFilterOptionCollapse();
   *       const state = this.filterStore.getState('collapseState') || {};
   *       Object.keys(state).forEach(key => {
   *         state[key].targets?.forEach(t => { t.shouldAddClass = false; });
   *       });
   *       this.filterStore.setState({ collapseState: state });
   *     }
   *   };
   * });
   * ```
   */
  initFilterOptionCollapse() {
    const filterState = this.filterStore.state.value.filter;
    const { options } = filterState;
    const {
      keepToggleState,
      filterLayout,
      filterTreeHorizontalStyle,
      filterTreeMobileStyle,
      filterTreeVerticalStyle,
    } = this.appService.filterSettings;

    if (!options) return;

    const optionsActive = options.filter((option) => option.status !== 'disabled');

    const _isMobile = this.filterHelper.isMobile();
    const _isDesktop = !_isMobile;

    // remove class hidden in mobile style1 - FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS
    if (['style1', 'style3-fullwidth', 'style3'].includes(filterTreeMobileStyle)) {
      const wrapperItemsStyle = document?.querySelectorAll(
        `${FILTER_MOBILE_WRAPPER_SELECTOR} .boost-sd__filter-option-itemwrapper`
      );

      if (wrapperItemsStyle?.length > 0) {
        wrapperItemsStyle?.forEach((wrapper) => {
          wrapper?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
        });
      }
    }

    const collapseState = this.filterStore.state.value.collapseState;

    let _getCollapseState = {};

    // If keepToggleState = true then when init/ recall-api state will merge current state else nothing
    if (keepToggleState) {
      _getCollapseState = collapseState;
    }

    const selectedFilters = this.filterStore.state.value.selectedFilter || [];

    optionsActive.forEach((option) => {
      if (!option.filterOptionId) return;
      requestIdleCallback(() => {
        const rawAction = `${option.filterOptionId?.replace(/::/g, '__')}-toggle`;
        const metadata = JSON.stringify({
          action: {
            filterOptionCollapse: {
              actionId: rawAction,
            },
          },
        });

        this.removeDomFilterOptionIfEmpty(rawAction, option);
        this.buildAttributeDataFilterOptionListItem(rawAction, option);
        this.buildEventFilterOptionCollapsed(rawAction, metadata);
        this.showHideSearchBoxOption(rawAction, option);

        if (!this.filterHelper.isMobile() && filterLayout === 'vertical') {
          const contextDocument = this.filterStore.state.value.document as HTMLElement;

          // Determine the query container based on filter style
          let queryContainer: HTMLElement | null = contextDocument;

          // For off-canvas style, query from the off-canvas panel
          if (filterTreeVerticalStyle === 'style-off-canvas') {
            const offCanvasPanel = contextDocument?.querySelector(
              '#boost-sd__filter-tree-wrapper #boost-sd__filter-tree-vertical--style-off-canvas'
            ) as HTMLElement | null;
            if (offCanvasPanel) {
              queryContainer = offCanvasPanel;
            }
          }

          const filterOptionBtnClear = queryContainer?.querySelector(
            `#${rawAction}-target-label .boost-sd__button--clear`
          ) as HTMLElement | null;

          if (filterOptionBtnClear) {
            const filterOptionSelected = selectedFilters.map((selected) => selected.data.key);

            if (!filterOptionBtnClear.dataset.metadata) {
              const metadata = JSON.stringify({
                action: {
                  resetFilterOption: { key: option.filterOptionId },
                  from: 'client',
                },
              });
              filterOptionBtnClear.setAttribute('data-metadata', metadata);
              filterOptionBtnClear.querySelector('span')?.setAttribute('data-metadata', metadata);
            }

            if (filterOptionSelected.includes(option.filterOptionId)) {
              filterOptionBtnClear.classList?.remove('boost-sd__g-hide');
            } else {
              filterOptionBtnClear.classList?.add('boost-sd__g-hide');
            }
          }
        }

        if (!this.filterHelper.isMobile() && filterLayout === 'horizontal') {
          // bind event to apply and reset button
          const applyId = this.filterHelper.simpleMd5(`${option.filterOptionId}-apply-button`);
          const clearId = this.filterHelper.simpleMd5(`${option.filterOptionId}-reset-button`);
          const metadataApply = JSON.stringify({
            action: {
              applyFilterOption: {
                key: option.filterOptionId,
                from: 'client',
              },
            },
          });

          const resetFilterOption = JSON.stringify({
            action: {
              resetFilterOption: {
                key: option.filterOptionId,
                from: 'client',
              },
            },
          });

          if (filterTreeHorizontalStyle !== 'style-expand') {
            const btnApply = document.querySelector(
              `${FILTER_DESKTOP_WRAPPER_SELECTOR} #${applyId}`
            ) as HTMLElement | null;
            const btnClear = document.querySelector(
              `${FILTER_DESKTOP_WRAPPER_SELECTOR} #${clearId}`
            ) as HTMLElement | null;

            if (btnApply && !btnApply.dataset.metadata)
              btnApply.setAttribute('data-metadata', metadataApply);
            if (btnClear && !btnClear.dataset.metadata)
              btnClear.setAttribute('data-metadata', resetFilterOption);
          }
        }
      });
    });

    // bind event for Apply and Clear All button in horizontal style-expand
    const isHorizontalStyleExpand =
      !this.filterHelper.isMobile() &&
      filterLayout === 'horizontal' &&
      filterTreeHorizontalStyle === 'style-expand';

    if (isHorizontalStyleExpand) {
      const dom = document.querySelector(`.boost-sd__filter-tree-horizontal-button`);
      if (dom) {
        dom.innerHTML =
          `
        <button id="boost-sd__apply-all-button" metadata='{"action": { "applyFilterOption": "applyAll" }}' type="button" class="boost-sd__button boost-sd__button--apply">
          <span class="boost-sd__button-text">` +
          this.filterTranslation.translateByKey('applyAll', 'Apply All') +
          `</span>
        </button>
        <button id="boost-sd__reset-all-button" metadata='{"action": { "clearAllFilter": "" }}' type="button" class="boost-sd__button boost-sd__button--clear">
            <span class="boost-sd__button-text">` +
          this.filterTranslation.translateByKey('clearAll', 'Clear All') +
          `</span>
        </button>
        `;
      }
    }

    // manager collapse/open based on state
    const collapseInitState = {};

    optionsActive?.forEach((option) => {
      if (!option.filterOptionId) return;
      const rawAction = `${option.filterOptionId.replace(/::/g, '__')}-toggle`;

      const targetId = `${rawAction}-target`;
      const targetIdLabel = `${rawAction}-target-label`;

      const actionId = rawAction;
      const isCollapsedHorizontal =
        _isDesktop && filterLayout === 'horizontal' && filterTreeHorizontalStyle !== 'style-expand';

      // init collapsed desktop vertical or mobile style1, style3, style3-fullwidth (Don't apply for mobile style2)
      if (
        (_isDesktop && filterLayout !== 'horizontal') ||
        (_isMobile && ['style1', 'style3-fullwidth', 'style3'].includes(filterTreeMobileStyle))
      ) {
        collapseInitState[actionId] = {
          actionId: actionId,
          filterLayout: filterLayout,
          targets: [
            {
              targetId: targetId, // id wrap filter option items
              classToggle: FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS,
              shouldAddClass:
                _isDesktop && option.isCollapsePC
                  ? true
                  : !_isDesktop && option.isCollapseMobile
                    ? true
                    : false,
            },
            {
              classToggle: 'boost-sd__filter-option-label--collapsed',
              targetId: targetIdLabel,
              shouldAddClass:
                _isDesktop && option.isCollapsePC
                  ? true
                  : !_isDesktop && option.isCollapseMobile
                    ? true
                    : false,
            },
          ],
        };
      }

      // Init collapsed horizontal style1, style2, style3 (! style-expand)
      if (isCollapsedHorizontal) {
        collapseInitState[actionId] = {
          actionId: actionId,
          filterLayout: filterLayout,
          targets: [
            {
              targetId: targetId,
              classToggle: FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS,
              shouldAddClass: true,
            },
            {
              classToggle: FILTER_OPTION_TITLE_OPENING_CLASS,
              targetId: targetIdLabel,
              shouldAddClass: false,
            },
          ],
        };
      }

      // Remove class hidden if template return have class hidden
      if (isHorizontalStyleExpand) {
        const filterTreeParent = this.filterService.getFilterTreeParent();
        filterTreeParent
          ?.querySelector(`#${targetId}`)
          ?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
      }
    });

    this.filterStore.setState({
      collapseState: {
        ...collapseInitState,
        ..._getCollapseState,
      },
    });

    this.filterHelper.emit(
      EVENT_NAMES.COLLAPSE_CHANGED,
      this.filterStore.state.value.collapseState
    );

    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    // ✅ Add Escape key support to collapse all open filter options
    contextDocument.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const focusedEl = document.activeElement;
        const filterBox = focusedEl?.closest('.boost-sd__filter-option');
        if (!filterBox) return;

        const actionId = filterBox.querySelector('.boost-sd__filter-option-title')?.id;
        if (!actionId) return;

        const collapseState = this.filterStore.state.value.collapseState;

        const currentAction = collapseState[actionId];
        if (!currentAction || !currentAction.targets) return;

        const updatedTargets = currentAction.targets.map((target) => {
          return {
            ...target,
            shouldAddClass: target.classToggle === FILTER_OPTION_TITLE_OPENING_CLASS ? false : true,
          };
        });

        const updatedAction = {
          ...currentAction,
          targets: updatedTargets,
        };

        this.handleCollapse(updatedAction);

        this.filterStore.setState({
          collapseState: {
            ...collapseState,
            [actionId]: updatedAction,
          },
        });

        this.filterHelper.emit(
          EVENT_NAMES.COLLAPSE_CHANGED,
          this.filterStore.state.value.collapseState
        );

        const toggleBtn = filterBox.querySelector(
          '.boost-sd__filter-option-title'
        ) as HTMLElement | null;

        if (toggleBtn?.getAttribute('aria-expanded') === 'true') {
          toggleBtn.setAttribute('aria-expanded', 'false');
        }

        toggleBtn?.focus();
      }
    });
  }

  /**
   * Applies a single collapse action to the filter tree DOM.
   *
   * Adds or removes CSS classes on the target elements specified in the action
   * object, scoped to the current filter tree parent element.
   *
   * @param action - Collapse action with target element IDs and class toggles
   *
   * @example
   * Override to add custom collapse tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
   *   return class extends FilterRender {
   *     handleCollapse(action) {
   *       super.handleCollapse(action);
   *       if (action?.actionId) {
   *         this.analytics.track('filter_collapsed', { id: action.actionId });
   *       }
   *     }
   *   };
   * });
   * ```
   */
  handleCollapse(action) {
    if (!action) return; // cover horizontal with style = style-expand
    const filterTreeParent = this.filterService.getFilterTreeParent();

    const targets = action.targets;
    if (targets.length > 0) {
      targets.forEach((target) => {
        const targetElement = filterTreeParent?.querySelector(`#${target.targetId}`);
        const shouldAddClass = target.shouldAddClass;

        if (targetElement) {
          shouldAddClass
            ? targetElement.classList.add(target.classToggle)
            : targetElement.classList.remove(target.classToggle);
        }
      });
    }
  }

  /**
   * Manages view more state and rendering for filter options.
   *
   * Controls the "View More" / "View Less" functionality that shows/hides additional
   * filter values when there are many options. Updates state and re-renders the
   * affected filter option with the appropriate number of values displayed.
   *
   * @param params - View more configuration parameters
   * @param params.label - Filter option label
   * @param params.values - Array of all filter values
   * @param params.swatchShape - Shape for swatch display (circle/square)
   * @param params.swatchType - Layout type for swatches (grid/list)
   * @param params.option - Complete filter option configuration
   * @param params.dom - DOM element containing the filter option
   * @param params.showDocCount - Whether to display document counts
   * @param params.selectedKeys - Map of selected filter value keys
   * @param params.classNameSelected - CSS class for selected items
   * @param params.classNameUnselected - CSS class for unselected items
   *
   * @remarks
   * This method initializes or updates the ViewMoreState in FilterStore and
   * subscribes to VIEWMORE_TRIGGERED events for dynamic updates.
   *
   * @example
   * Override to customize view more behavior:
   * ```typescript
   * async manageViewMoreState(params) {
   *   // Change default number of visible items
   *   const customParams = {
   *     ...params,
   *     option: {
   *       ...params.option,
   *       numberViewMore: this.getCustomViewMoreCount(params.option.filterType)
   *     }
   *   };
   *   return super.manageViewMoreState(customParams);
   * }
   * ```
   */
  async manageViewMoreState(params: {
    label: string;
    values: any[];
    swatchShape?: string;
    swatchType?: string;
    option: any;
    dom: HTMLElement;
    showDocCount?: boolean;
    selectedKeys: Record<string, boolean>;
    classNameSelected: string;
    classNameUnselected: string;
  }): Promise<void> {
    const {
      label,
      values,
      swatchShape,
      swatchType,
      option,
      dom,
      showDocCount,
      selectedKeys,
      classNameSelected,
      classNameUnselected,
    } = params;

    const isFilterTreeVertical = this.appService.filterSettings?.filterLayout === 'vertical';
    const numberViewMore = this.getNumberViewMore(option, isFilterTreeVertical);
    const viewMoreState = this.filterStore.getState('viewMoreState') || {};

    const filterOptionContentInner = dom.closest('.boost-sd__filter-option-content-inner');
    const viewMoreBtnDom = filterOptionContentInner?.nextElementSibling as HTMLElement | null;

    const isViewMoreOption = this.isViewMoreFilterOption(option);

    viewMoreState[option.filterOptionId] = {
      label,
      values,
      numberViewMore,
      enabled: isViewMoreOption && values.length > numberViewMore,
      isExpanded: viewMoreState[option.filterOptionId]?.isExpanded || false,
      showMoreType: option.showMoreType,
      dom,
      viewMoreBtnDom,
      option,
      swatchShape,
      swatchType,
      showDocCount,
      selectedKeys,
      classNameSelected,
      classNameUnselected,
      lastClickedTime: Date.now(),
    };

    this.filterStore.setState({ viewMoreState });

    // Add click event handler
    if (viewMoreBtnDom) {
      const onViewMoreClick = () => {
        const currentViewMoreState = this.filterStore.getState('viewMoreState') || {};
        const filterOptionState = currentViewMoreState[option.filterOptionId];
        const now = Date.now();
        const debounceTime =
          (window as any).boostWidgetIntegrationConfig?.viewMoreClickDebounceTime || 300;

        // Avoid multiple clicks
        if (now - filterOptionState.lastClickedTime > debounceTime) {
          filterOptionState.isExpanded = !filterOptionState.isExpanded;
          filterOptionState.values = option.values;
          filterOptionState.lastClickedTime = now;

          this.filterStore.setState({
            viewMoreState: currentViewMoreState,
            viewMoreTrigger: {
              filterOptionId: option.filterOptionId,
              displayType: option.displayType,
            },
          });
          this.filterHelper.emit(EVENT_NAMES.VIEWMORE_TRIGGERED, option);
        }
      };

      // Remove existing listener if any
      const oldListener = (viewMoreBtnDom as any)._viewMoreClickListener;
      if (oldListener) {
        viewMoreBtnDom.removeEventListener('click', oldListener);
      }

      viewMoreBtnDom.addEventListener('click', onViewMoreClick);
      (viewMoreBtnDom as any)._viewMoreClickListener = onViewMoreClick;
    }

    // Initial render
    await this.renderViewMore(option.filterOptionId, option.displayType);
  }

  /**
   * Renders the updated filter option values when view more is toggled.
   *
   * Retrieves the current view more state, determines which values to display
   * (limited or all), and re-renders the filter option using the appropriate
   * template (list, box, or swatch).
   *
   * @param filterOptionId - Unique identifier for the filter option
   * @param filterOptionDisplayType - Display type (list/box/swatch)
   *
   * @remarks
   * This method is called by the view more button click handler and updates
   * the button text between "View More" and "View Less".
   *
   * @example
   * Override to add custom rendering logic:
   * ```typescript
   * async renderViewMore(filterOptionId, filterOptionDisplayType) {
   *   await super.renderViewMore(filterOptionId, filterOptionDisplayType);
   *   // Add custom animation
   *   const container = document.querySelector(`[data-filter-option="${filterOptionId}"]`);
   *   container?.classList.add('animate-expand');
   * }
   * ```
   */
  async renderViewMore(filterOptionId: string, filterOptionDisplayType: string): Promise<void> {
    const viewMoreState = this.filterStore.getState('viewMoreState') || {};
    const updatedState = viewMoreState[filterOptionId];

    if (!updatedState) return;

    const {
      isExpanded,
      viewMoreBtnDom,
      enabled,
      numberViewMore,
      option: { displayType },
    } = updatedState;

    let { values } = updatedState;

    if (displayType !== filterOptionDisplayType) return;

    // Update button text and values
    if (enabled && viewMoreBtnDom) {
      viewMoreBtnDom.style.display = 'block';
      if (isExpanded) {
        viewMoreBtnDom.textContent = this.filterTranslation.translateByKey('viewLess', 'View Less');
      } else {
        values = values.slice(0, numberViewMore);
        viewMoreBtnDom.textContent = this.filterTranslation.translateByKey('viewMore', 'View More');
      }
    }

    // Render based on display type
    const renderParams = {
      option: updatedState.option,
      values,
      dom: updatedState.dom,
      label: updatedState.label,
      selectedKeys: updatedState.selectedKeys,
      classNameSelected: updatedState.classNameSelected,
      classNameUnselected: updatedState.classNameUnselected,
    };

    switch (displayType) {
      case 'list':
        await this.renderOptionListTemplate({
          ...renderParams,
          showDocCount: updatedState.showDocCount || false,
        });
        break;

      case 'box':
        await this.renderOptionBoxTemplate(renderParams);
        break;

      case 'swatch':
        await this.renderOptionSwatchTemplate({
          ...renderParams,
          swatchShape: updatedState.swatchShape || 'circle',
          swatchType: updatedState.swatchType || 'grid',
          showDocCount: updatedState.showDocCount || false,
        });
        break;

      default:
        break;
    }
  }

  // ============================================
  // Collection Filter Methods
  // ============================================

  /**
   * Main handler for collection option clicks.
   *
   * Routes collection filter selections to the appropriate handler based on
   * current page context. On collection pages, redirects to the selected collection.
   * On search pages, updates filters to show products from the selected collection.
   *
   * @param action - Collection selection action data
   * @param action.key - Collection identifier key
   * @param action.value - Collection value
   * @param action.handle - Collection handle for URL
   * @param action.collectionTag - Optional collection tag
   * @param action.displayName - Display name for the collection
   * @param action.valueDisplay - Display value
   *
   * @remarks
   * Automatically scrolls to top before processing the collection change.
   * Override this to customize collection filter behavior per shop.
   *
   * @example
   * Override to add custom analytics:
   * ```typescript
   * async handleCollectionOption(action) {
   *   // Track collection selection
   *   this.analytics.track('collection_filter_clicked', {
   *     collection: action.handle,
   *     context: this.isCollectionPage() ? 'collection' : 'search'
   *   });
   *   return super.handleCollectionOption(action);
   * }
   * ```
   */
  async handleCollectionOption(action: {
    key: string;
    value: string;
    handle: string;
    collectionTag?: string;
    displayName?: string;
    valueDisplay?: string;
  }): Promise<void> {
    this.domHelper.scrollToTop({ isFilter: true });

    if (this.isCollectionPage()) {
      await this.redirectCollection(action);
    } else {
      await this.searchPageCollection(action);
    }
  }

  /**
   * @deprecated Use {@link handleCollectionOption} instead.
   * This method does not await the async collection handlers, silently swallowing
   * any errors. Retained for backward compatibility only.
   */
  optionCollection(action) {
    this.domHelper.scrollToTop({ isFilter: true });

    // redirect collection only collection page
    if (this.isCollectionPage()) {
      this.redirectCollection(action);
    } else {
      // logic search page - still show in refine by
      this.searchPageCollection(action);
    }
  }

  /**
   * Redirects to collection page (for collection page context)
   */
  protected async redirectCollection(action: {
    key: string;
    value: string;
    handle: string;
    collectionTag?: string;
  }): Promise<void> {
    const { key, value, handle, collectionTag } = action;

    // Update selected classes
    this.addFilterListSelectedClass(key, value);
    const currentCollectionId = this.appService.TAEAppConfig.generalSettings.collection_id || 0;
    if (currentCollectionId) {
      this.removeFilterListSelectedClass(key, String(currentCollectionId));
    }

    // Build new URL
    let newUrl = this.buildUrlWithLocalization(`collections/${handle}`);

    // Update collection state in TAEAppConfig
    this.appService.TAEAppConfig.generalSettings.collection_id = parseInt(String(value));
    this.appService.TAEAppConfig.generalSettings.collection_handle = handle;

    // Save collection handle→id mapping to sessionStorage before parseCollectionPath,
    // so it can resolve the collection_id from the new handle
    this.filterHelper.saveCollectionIdByHandle(handle, String(value));

    if (collectionTag) {
      this.filterHelper.saveCollectionTagsSlugKey(
        this.filterHelper.slugify(collectionTag),
        collectionTag
      );
      this.addFilterListSelectedClass(FILTER_TAG_KEY, collectionTag);

      newUrl = `${newUrl}/${this.filterHelper.slugify(collectionTag)}`;
      this.appService.TAEAppConfig.generalSettings.collection_tags = [collectionTag];
    } else {
      this.appService.TAEAppConfig.generalSettings.collection_tags = undefined;
    }

    // Push to history
    history.pushState({}, '', newUrl);

    // Parse collection path from URL to ensure consistency with established pattern
    // This validates that TAEAppConfig state matches the URL structure
    this.filterService.parseCollectionPath();

    // Render collection header if available
    this.renderCollectionHeader();

    // Clear all filters (prevent history push since we already pushed)
    await this.clearAllFilters({ preventPushHistory: true });

    // Dispatch analytics event
    this.dispatchEventReViewCollection();
  }

  /**
   * Handles collection filter in search page context
   */
  protected async searchPageCollection(action: {
    key: string;
    value: string;
    handle: string;
    collectionTag?: string;
    displayName?: string;
    valueDisplay?: string;
  }): Promise<void> {
    const { key, value, handle, collectionTag, displayName, valueDisplay } = action;
    const collectionDisplay = valueDisplay || displayName;

    // Get current filter tree from store
    const currentFilters = this.filterStore.getState('selectedFilter') || [];
    const { isShortenUrlParam } = this.appService.filterSettings;

    // Filter out old collection and tag filters
    const oldParams = currentFilters.filter(
      (item: any) => item.data.key !== key && item.data.key !== FILTER_TAG_KEY
    );

    const newFilterTree = [...oldParams];
    const collection = {
      data: {
        label: 'Collection',
        value,
        key,
        valueDisplay: collectionDisplay,
        handle,
      },
      metaData: {
        key,
        value,
        dependencies: undefined as any,
      },
      type: 'list',
    };

    if (collectionTag) {
      const tag = {
        data: {
          label: 'Collection Tag',
          value: collectionTag,
          key: FILTER_TAG_KEY,
          valueDisplay: collectionTag,
        },
        metaData: {
          key: FILTER_TAG_KEY,
          value: collectionTag,
          dependencies: [{ key, value }],
        },
        type: 'list',
      };
      collection.metaData.dependencies = [{ key: FILTER_TAG_KEY, value: collectionTag }];
      newFilterTree.push(collection);
      newFilterTree.push(tag);
    } else {
      newFilterTree.push(collection);
    }

    // Update store
    this.filterStore.setState({ selectedFilter: newFilterTree });

    // Update URL params
    this.filterHelper.setQueryParamHistory(key, value, false, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });

    if (!collectionTag) {
      this.filterHelper.deleteQueryParamHistory(FILTER_TAG_KEY, {
        preventPushHistory: true,
        isShortenUrlParam,
        urlScheme: this.filterHelper.getUrlScheme(),
      });
    } else {
      this.filterHelper.setQueryParamHistory(FILTER_TAG_KEY, collectionTag, false, {
        preventPushHistory: true,
        isShortenUrlParam,
        urlScheme: this.filterHelper.getUrlScheme(),
      });
    }

    // Reset filters and re-fetch
    await this.filterService.resetFilters();

    // Render selected options
    this.renderSelectedOption();
  }

  // ============================================
  // Helper Methods for Collection
  // ============================================

  /**
   * Checks if the current page is a collection page.
   *
   * @returns `true` if the current URL pathname contains `/collections/`
   */
  protected isCollectionPage(): boolean {
    return window.location.pathname.includes('/collections/');
  }

  /**
   * Builds a URL with a localization prefix when necessary.
   *
   * Detects a 2-letter ISO locale code (e.g., `fr`, `de`) from the current
   * pathname and prepends it to the given path. English (`en`) is excluded
   * because Shopify typically omits it from URLs.
   *
   * @param path - The path to prefix (e.g., `"collections/new-arrivals"`)
   * @returns The full path with optional locale prefix (e.g., `"/fr/collections/new-arrivals"`)
   */
  protected buildUrlWithLocalization(path: string): string {
    // Get current pathname and split into parts
    const pathParts = window.location.pathname.split('/').filter(Boolean);

    // Check if first part is a locale code (typically 2-letter ISO code like 'en', 'fr', etc.)
    const possibleLocale = pathParts[0];
    const isLocale =
      possibleLocale && possibleLocale.length === 2 && /^[a-z]{2}$/i.test(possibleLocale);

    if (isLocale && possibleLocale !== 'en') {
      return `/${possibleLocale}/${path}`;
    }

    return `/${path}`;
  }

  /**
   * Renders the collection header with title, description, and metadata.
   *
   * Dynamically generates the collection header HTML including the collection name,
   * description, image, and product count. Handles both collection pages and
   * collection-filtered search results.
   *
   * @remarks
   * The header is rendered into the #boost-sd__collection-header element and
   * includes the collection's body HTML if available. Override this method to
   * customize header layout or add shop-specific information.
   *
   * @example
   * Override to add custom header content:
   * ```typescript
   * async renderCollectionHeader() {
   *   await super.renderCollectionHeader();
   *   const header = document.querySelector('#boost-sd__collection-header');
   *   if (header && this.shouldShowPromo()) {
   *     header.insertAdjacentHTML('beforeend',
   *       '<div class="collection-promo">Special offer on this collection!</div>'
   *     );
   *   }
   * }
   * ```
   */
  async renderCollectionHeader(): Promise<void> {
    const additionalElements = this.appService.themeSettings?.additionalElements;

    // Check if collection header is enabled
    if (
      !additionalElements?.collectionHeader?.isEnable &&
      !additionalElements?.collectionHeader?.elements?.breadCrumb?.isEnable
    ) {
      return;
    }

    // Get template ID and themeNameLib
    const templateMetadata = this.appService.TAEAppConfig.templateMetadata as Record<string, any>;
    const templateId = templateMetadata?.collectionHeader || 'collection_header_id';

    // Fetch template from API (with caching)
    const templatePromise = this.filterHelper.getTemplateByIds({
      name: 'collectionHeader',
      id: templateId,
    });

    // Get collection data (async API call)
    const collectionPromise = this.getCollectionData();

    // Combine both promises for parallel execution
    const htmlPromise = Promise.all([templatePromise, collectionPromise]).then(
      async ([templateResponse, collectionData]) => {
        const template = templateResponse.templates[templateId];
        const html = await this.appService.templateRender(template, collectionData);
        return html;
      }
    );

    // Render when ready (non-blocking)
    htmlPromise
      .then((html) => {
        const docContainer = this.filterStore.getState('document');
        const dom = docContainer.querySelector('#boost-sd__collection-header');
        if (dom) {
          dom.innerHTML = html;
          // Apply parallax effect after DOM injection
          this.applyParallaxEffect();
        }
      })
      .catch((error) => {
        console.error('Failed to render collection header:', error);
      });
  }

  /**
   * Gets collection data for header
   * Migrated from collection-header/index.js
   */
  protected async getCollectionData(params: { page?: number } = {}): Promise<any> {
    const productList = this.appService.themeSettings?.productList;
    const productPerPage = productList?.productsPerPage || 16;
    const selectedValueOfLimitList =
      this.filterHelper.getLocalStorage(LIMIT) || String(productPerPage);
    const limitSetting = this.filterHelper.getLocalStorage(LIMIT_SETTING);
    const isLimitSettingEqual = limitSetting === String(productPerPage);
    const defaultLimit = 16;

    const limit = isLimitSettingEqual
      ? parseInt(selectedValueOfLimitList, 10)
      : productPerPage || defaultLimit;

    // Get page from params or history (same as original index.js)
    const page = params.page || this.filterHelper.getParamsHistory().pagination.page || 1;

    const url = `${window.location.origin}${window.location.pathname}.json`;
    const queryParams = new URLSearchParams({
      limit: String(limit),
      page: String(page),
    });

    const collectionId = this.appService.TAEAppConfig.generalSettings?.collection_id;

    // Get product count from filter store document (virtual DOM) instead of global document
    const filterDocument = this.filterStore.state.value.document as HTMLElement;
    const productListElement = filterDocument?.querySelector('.boost-sd__product-list');
    const rawTotal = productListElement?.getAttribute('data-total-product');
    const parsedTotal = Number(rawTotal);
    const productCount = Number.isNaN(parsedTotal) ? 0 : parsedTotal;
    // If no collection ID, return default data
    if (!collectionId) {
      const title = this.filterTranslation.translateByKey(
        'collectionHeader.collectionAllProduct',
        'Products'
      );
      return {
        title,
        products_count: productCount,
        breadcrumb_title: 'All',
        request: {
          limit,
          page,
        },
      };
    }

    // Use .then() pattern like original index.js
    return fetch(`${url}?${queryParams.toString()}`).then(async (res) => {
      if (res.status === 200) {
        const toJson = await res.json();
        toJson.collection.products_count = productCount;

        // Preload collection image before DOM injection
        if (toJson.collection.image?.src) {
          const tempImg = new Image();
          tempImg.src = toJson.collection.image.src; // cache image before inject collection header into dom
        }

        return {
          ...toJson.collection,
          breadcrumb_title: toJson.collection.title,
          request: {
            limit,
            page,
          },
        };
      }

      // Fallback if fetch fails
      const title = this.filterTranslation.translateByKey(
        'collectionHeader.collectionAllProduct',
        'Products'
      );
      return {
        title,
        products_count: productCount,
        breadcrumb_title: 'All',
        request: {
          limit,
          page,
        },
      };
    });
  }

  /**
   * Applies parallax effect to collection header image
   * Migrated from collection-header/parallax.js
   */
  protected applyParallaxEffect(): void {
    const themeSettings = this.appService.themeSettings as any;
    const additionalElements = themeSettings?.additionalElements;
    const collectionImage = additionalElements?.collectionHeader?.elements?.collectionImage;
    const { parallaxEffect: isParallaxEnabled, directionParallax } = collectionImage || {};

    if (!isParallaxEnabled) {
      return;
    }

    const collectionImg = document.querySelector('.boost-sd__header-image-inner') as HTMLElement;

    if (!collectionImg) {
      console.log('Collection image element not found for parallax effect.');
      return;
    }

    const parentContainer = collectionImg.parentElement as HTMLElement;
    if (!parentContainer) {
      console.log('Parent container not found.');
      return;
    }

    // Setup parent container styles
    const containerStyles = {
      position: 'relative',
      overflow: 'hidden',
    };

    // Setup image styles
    const imageStyles = {
      position: 'absolute',
      top: '0',
      left: '0',
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      willChange: 'transform',
      transform: `scale(${INITIAL_SCALE})`,
      transformOrigin: 'top center',
      transition: 'none',
    };

    Object.assign(parentContainer.style, containerStyles);
    Object.assign(collectionImg.style, imageStyles);

    const updateParallax = () => {
      const rect = parentContainer.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const elementTop = rect.top;
      const elementHeight = rect.height;

      // Only apply parallax when element is in view
      if (elementTop > viewportHeight || elementTop + elementHeight < 0) {
        return;
      }

      // Calculate the relative position of the element in the viewport
      const relativePos = elementTop / viewportHeight;

      // Calculate parallax offset
      const offset = relativePos * PARALLAX_SPEED * elementHeight;

      // Apply transform based on direction
      const translateValue =
        directionParallax === 'horizontal'
          ? `translate3d(-${offset}px, 0, 0) scale(${INITIAL_SCALE})`
          : `translate3d(0, -${offset}px, 0) scale(${INITIAL_SCALE})`;

      collectionImg.style.transform = translateValue;
    };

    // Throttle scroll handler for performance
    let ticking = false;
    const scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          updateParallax();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Handle window resize
    const resizeHandler = () => {
      Object.assign(parentContainer.style, containerStyles);
      updateParallax();
    };

    // Initial update
    updateParallax();

    // Event listeners
    window.addEventListener('scroll', scrollHandler, { passive: true });
    window.addEventListener('resize', resizeHandler, { passive: true });

    // Store cleanup function
    (collectionImg as any)._parallaxCleanup = () => {
      window.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', resizeHandler);

      // Reset styles on cleanup using empty string values
      const cleanupStyles = {
        container: {
          position: '',
          overflow: '',
          height: '',
        },
        image: {
          position: '',
          top: '',
          left: '',
          width: '',
          height: '',
          objectFit: '',
          willChange: '',
          transform: '',
          transformOrigin: '',
          transition: '',
        },
      };

      // Apply cleanup styles
      Object.assign(parentContainer.style, cleanupStyles.container);
      Object.assign(collectionImg.style, cleanupStyles.image);
    };
  }

  /**
   * Renders selected options in the filter tree UI
   * Adds selected class to their DOM elements
   */
  renderSelectedOption(): void {
    const selectedFilters = this.filterStore.getState('selectedFilter') || [];

    if (Array.isArray(selectedFilters)) {
      selectedFilters
        .filter((selected) => selected && selected.data && selected.data.key)
        .forEach((selected) => {
          this.addFilterSelectedClass(
            selected.data.key,
            selected.data.value,
            selected.metaData?.type
          );
        });
    }
    this.filterHelper.emit(EVENT_NAMES.FILTER_SELECTED);
  }

  /**
   * Dispatches analytics event for collection view
   */
  protected dispatchEventReViewCollection(): void {
    // Dispatch custom event for analytics
    const event = new CustomEvent('boost:collection:viewed', {
      detail: {
        timestamp: Date.now(),
        collectionId: this.appService.TAEAppConfig.generalSettings?.collection_id,
        collectionHandle: this.appService.TAEAppConfig.generalSettings?.collection_handle,
      },
    });
    window.dispatchEvent(event);
  }

  // ============================================
  // Fill Filter Tree Methods
  // ============================================

  /**
   * Fills filter tree with complete data from available options
   * Enriches selected filters with missing display values and metadata
   *
   * @remarks
   * Migrated from fillFilterTree in index.js
   * - Updates filter tree state with complete option data
   * - Handles both immediate fill and deferred fill via state watch
   * - Processes options through updateValuesOptions for filtering
   * - Enriches selected filters with display values from options
   */
  fillFilterTree(): void {
    const filterState = this.filterStore.getState('filter');
    const filterOptions = filterState?.options;

    // Case: state has filter tree data
    if (filterOptions) {
      this.handleFillFilterTree();
      return;
    }

    // Case: state does not have filter tree yet, so we need to watch for it
    // In the new architecture, we'll handle this via the FILTER_CHANGED event
    // The event handler will call handleFillFilterTree when filter tree is available
  }

  /**
   * Handles the actual filling of filter tree with option data
   *
   * @remarks
   * - Gets current settings for showOutOfStockOption and hideSingleOption
   * - Updates option values based on settings
   * - Fills missing values for selected filters
   * - Updates selectedFilter state with enriched data
   */
  protected handleFillFilterTree(): void {
    // Name is showSingleOption but data return from BE is hideSingleOption
    const { showOutOfStockOption, showSingleOption: hideSingleOption } =
      this.appService.filterSettings;

    const settingOptionValues = {
      showOutOfStockOption,
      hideSingleOption,
    };

    const filterState = this.filterStore.getState('filter');
    const filterOptions = filterState?.options || [];
    const selectedFilters = this.filterStore.getState('selectedFilter') || [];

    const newFilter = this.filterService.fillFilterTree(
      filterOptions,
      selectedFilters,
      settingOptionValues
    );
    this.filterStore.setState({ selectedFilter: newFilter });
  }

  // ============================================
  // Filter Tree Lazy Loading
  // ============================================

  /**
   * Handles responsive behavior for filter tree
   * Manages display switching between mobile and desktop layouts
   */
  handleFilterTreeResponsive(): void {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const mobileParent = contextDocument?.querySelector(
      FILTER_MOBILE_WRAPPER_SELECTOR
    ) as HTMLElement | null;
    const desktopParent = contextDocument?.querySelector(
      FILTER_DESKTOP_WRAPPER_SELECTOR
    ) as HTMLElement | null;

    const { filterLayout, filterTreeHorizontalStyle, filterTreeMobileStyle } =
      this.appService.filterSettings;

    if (!mobileParent || !desktopParent) {
      return;
    }

    const isMobileSize = this.filterHelper.isMobile();

    // Hide desktop on mobile
    if (isMobileSize && desktopParent && desktopParent.style.display !== 'none') {
      desktopParent.style.display = 'none';
    }

    // Show desktop on larger screens
    if (!isMobileSize && desktopParent && desktopParent.style.display === 'none') {
      this.closeMobileFilterTree();
      desktopParent.style.display = 'unset';

      // Only render once on first load
      if (!(window as any).renderFilterTreeFirstLoad) {
        this.lazyLoadFilterTree();
        (window as any).renderFilterTreeFirstLoad = true;
      }
    }

    const isFilterTreeMobileStyle1 = filterTreeMobileStyle === 'style1';
    const isFilterTreeMobileStyle2 =
      filterTreeMobileStyle === 'style2' || filterTreeMobileStyle === 'style2-fullwidth';
    const isFilterTreeMobileStyle3 =
      filterTreeMobileStyle === 'style3' || filterTreeMobileStyle === 'style3-fullwidth';
    const isDesktopHorizontal = !isMobileSize && filterLayout === 'horizontal';
    const filterOptionListHorizontal = '.boost-sd__filter-tree-horizontal-option-list';
    const filterOptionListMobile =
      '#boost-sd_filter-tree-mobile .boost-sd__filter-tree-vertical-option-list';
    const filterOptionWrapper = mobileParent?.querySelector(
      '.boost-sd__filter-option-itemwrapper--full-height'
    ) as HTMLElement | null;

    // Handle mobile style1 and style3
    if ((isFilterTreeMobileStyle3 || isFilterTreeMobileStyle1) && isMobileSize) {
      this.toggleHiddenClass('remove', filterOptionListMobile);
    }

    // Handle mobile style2
    if (isFilterTreeMobileStyle2 && isMobileSize) {
      this.toggleHiddenClass('add', filterOptionListMobile);
      filterOptionWrapper?.classList?.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
    }

    // Handle desktop horizontal layout
    if (isDesktopHorizontal) {
      if (filterTreeHorizontalStyle === 'style-expand') {
        this.toggleHiddenClass('remove', filterOptionListHorizontal);
      } else {
        this.toggleHiddenClass('add', filterOptionListHorizontal);
      }
    }
  }

  /**
   * Toggles hidden class on filter option item wrappers
   */
  protected toggleHiddenClass(action: 'add' | 'remove', targetSelector: string): void {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const targetElement = contextDocument?.querySelector(targetSelector);
    const filterOptionItemWrapper = '.boost-sd__filter-option-itemwrapper';

    if (targetElement) {
      const wrappers = targetElement.querySelectorAll<HTMLElement>(filterOptionItemWrapper);
      wrappers.forEach((element) => {
        if (action === 'add') {
          element.classList.add(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
        } else if (action === 'remove') {
          element.classList.remove(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
        }
      });
    }
  }

  /**
   * Closes mobile filter tree
   */
  closeMobileFilterTree(): void {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const mobile = contextDocument?.querySelector(
      FILTER_MOBILE_WRAPPER_SELECTOR
    ) as HTMLElement | null;

    if (mobile) {
      mobile.style.display = 'none';
    }
    document.body?.classList?.remove('boost-sd__g-no-scroll');
  }

  /**
   * Shows/toggles mobile filter tree
   * Handles display toggle, scroll lock, and lazy loading
   */
  showMobileFilterTree(): void {
    const { filterTreeMobileStyle } = this.appService.filterSettings;
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const filterTreeMobileWrapper = contextDocument?.querySelector(
      FILTER_MOBILE_WRAPPER_SELECTOR
    ) as HTMLElement | null;

    if (!filterTreeMobileWrapper) return;

    // Toggle display
    if (filterTreeMobileWrapper.style.display === 'none') {
      filterTreeMobileWrapper.style.display = 'block';
    } else {
      filterTreeMobileWrapper.style.display = 'none';
    }

    // Handle scroll lock and icon toggle based on style
    if (filterTreeMobileStyle !== 'style1') {
      document.body.classList.add('boost-sd__g-no-scroll');
    } else {
      this.toggleFilterTreeIcon();
    }

    // Trigger lazy load
    this.filterHelper.emit(EVENT_NAMES.FILTER_CHANGED);
  }

  /**
   * Toggles filter tree icon active state
   * Updates tabindex for accessibility when filter button is clicked
   */
  protected toggleFilterTreeIcon(): void {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const isMobileSize = this.filterHelper.isMobile(
      parseInt(
        (this.appService.TAEAppConfig.generalSettings as { breakpointtabletportraitmax?: string })
          ?.breakpointtabletportraitmax || '768',
        10
      )
    );

    const filterTreeToggleButton = isMobileSize
      ? contextDocument?.querySelector(
          '.boost-sd__toolbar-top-mobile .boost-sd__filter-tree-toggle-button'
        )
      : contextDocument?.querySelector('.boost-sd__filter-tree-toggle-button--expand');

    filterTreeToggleButton?.classList?.toggle('boost-sd__filter-tree-toggle-button--active');

    const { stickyFilterOnMobile } = this.appService.filterSettings;
    if (stickyFilterOnMobile) {
      // Handle sticky filter tree for mobile expand content
      this.filterHelper.emit(EVENT_NAMES.LOAD_STICKY_MOBILE);
    }

    // Toggle tabindex on toolbar elements when the filter button is clicked
    const toolbarElements = document.querySelectorAll('.boost-sd__toolbar [tabindex]');
    const updateTabIndex = (element: Element) => {
      const tabIndex = element.getAttribute('tabindex');
      if (tabIndex === '0') {
        element.setAttribute('tabindex', '-1');
        filterTreeToggleButton?.removeAttribute('tabindex');
      } else if (tabIndex === '-1') {
        element.setAttribute('tabindex', '0');
        filterTreeToggleButton?.setAttribute('tabindex', '0');
        (filterTreeToggleButton as HTMLElement)?.focus();
      }
    };

    if (toolbarElements.length > 0) {
      toolbarElements.forEach(updateTabIndex);
    }
  }

  /**
   * Toggles desktop filter tree button visibility and state
   * Handles horizontal, vertical expand, and vertical off-canvas layouts
   */
  toggleFilterTreeButtonDesktop(): void {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const filterTreeParent = this.filterService.getFilterTreeParent();
    const { filterLayout, filterTreeVerticalStyle, stickyFilterOnDesktop } =
      this.appService.filterSettings;

    // Get current desktop opening state
    const desktopOpeningState =
      (this.filterStore.getState('filterTreeDesktopOpening') as boolean) || false;

    if (filterLayout === 'horizontal') {
      // Handle horizontal layout - Show all filter options style
      const styleShowAllOptionSelector =
        '#boost-sd__filter-tree-wrapper .boost-sd__filter-tree-horizontal';
      const targetElement = filterTreeParent?.querySelector(
        styleShowAllOptionSelector
      ) as HTMLElement | null;

      if (desktopOpeningState) {
        targetElement?.classList?.add('boost-sd__filter-tree-horizontal--hidden');
      } else {
        targetElement?.classList?.remove('boost-sd__filter-tree-horizontal--hidden');
        if (stickyFilterOnDesktop && !this.filterHelper.isMobile()) {
          // Handle sticky filter tree for horizontal layout
          this.filterHelper.emit(EVENT_NAMES.LOAD_STICKY_HORIZONTAL_DESKTOP);
        }
      }

      this.toggleFilterTreeIcon();
      this.filterStore.setState({ filterTreeDesktopOpening: !desktopOpeningState });
    } else {
      // Handle vertical layout

      // Style: Collapsed - Expand
      if (filterTreeVerticalStyle === 'style-expand') {
        const styleCollapseExpandSelector = '.boost-sd__filter-tree-vertical';
        const targetElement = filterTreeParent?.querySelector(
          styleCollapseExpandSelector
        ) as HTMLElement | null;

        if (desktopOpeningState) {
          targetElement?.classList?.add('boost-sd__filter-tree-vertical--hidden');
        } else {
          targetElement?.classList?.remove('boost-sd__filter-tree-vertical--hidden');
          if (stickyFilterOnDesktop && !this.filterHelper.isMobile()) {
            // Handle sticky filter tree for vertical expand
            this.filterHelper.emit(EVENT_NAMES.LOAD_STICKY_VERTICAL_DESKTOP);
          }
        }

        this.toggleFilterTreeIcon();
        this.filterStore.setState({ filterTreeDesktopOpening: !desktopOpeningState });
      }

      // Style: Off canvas - Left side
      if (filterTreeVerticalStyle === 'style-off-canvas') {
        const styleOffCanvasSelector =
          '#boost-sd__filter-tree-wrapper #boost-sd__filter-tree-vertical--style-off-canvas';
        const targetElement = contextDocument?.querySelector(
          styleOffCanvasSelector
        ) as HTMLElement | null;

        if (desktopOpeningState) {
          targetElement?.classList?.add('boost-sd__filter-tree-vertical--hidden');
        } else {
          targetElement?.classList?.remove('boost-sd__filter-tree-vertical--hidden');
        }

        this.toggleFilterTreeIcon();
        this.filterStore.setState({ filterTreeDesktopOpening: !desktopOpeningState });
        document.body.classList.add('boost-sd__g-no-scroll');
      }
    }
  }

  /**
   * Handles showing results for off-canvas filter
   * Closes the off-canvas filter when clicking result button, close button, or overlay
   *
   * @param target - The clicked element
   */
  handleShowResult(target: HTMLElement): void {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const { filterLayout, filterTreeVerticalStyle } = this.appService.filterSettings;
    const isMobileSize = this.filterHelper.isMobile();

    // Only handle for vertical off-canvas layout on desktop
    const filterTreeVerticalStyleOffCanvas =
      filterLayout === 'vertical' && filterTreeVerticalStyle === 'style-off-canvas';

    if (!filterTreeVerticalStyleOffCanvas || isMobileSize) {
      return;
    }

    // Check if clicked on result button, close button, or overlay
    const isResultButton = target.closest('.boost-sd__button.boost-sd__button--result');
    const isCloseButton = target.closest('.boost-sd__filter-tree-vertical-close-filter--left');
    const isOverlay = target.classList.contains('boost-sd__filter-tree-vertical-sticky-overlay');

    if (isResultButton || isCloseButton || isOverlay) {
      const styleOffCanvasSelector =
        '#boost-sd__filter-tree-wrapper #boost-sd__filter-tree-vertical--style-off-canvas';
      const styleOffCanvas = contextDocument?.querySelector(
        styleOffCanvasSelector
      ) as HTMLElement | null;

      // Hide the off-canvas filter
      styleOffCanvas?.classList?.add('boost-sd__filter-tree-vertical--hidden');

      // Update state
      this.filterStore.setState({ filterTreeDesktopOpening: false });

      // Toggle icon
      this.toggleFilterTreeIcon();

      // Remove scroll lock
      document.body.classList.remove('boost-sd__g-no-scroll');
    }
  }

  /**
   * Navigates back in mobile filter tree
   * Resets mobile filter state to show all filter options
   */
  backFilterTreeMobile(): void {
    const { filterTreeMobileStyle } = this.appService.filterSettings;
    const contextDocument = this.filterStore.state.value.document as HTMLElement;

    // Update refine by title for non-style1
    if (filterTreeMobileStyle !== 'style1') {
      const refineByMobileTitle = contextDocument?.querySelector(
        '.boost-sd__filter-tree-vertical-toolbar-middle'
      ) as HTMLElement | null;

      if (refineByMobileTitle) {
        refineByMobileTitle.innerHTML = this.filterTranslation.translateByKey(
          'filterTreeMobile',
          'Refine by'
        );
      }
    }

    // Show clear all button if filters are selected (for style2 and style3)
    if (
      ['style2', 'style2-fullwidth', 'style3', 'style3-fullwidth'].includes(filterTreeMobileStyle)
    ) {
      const selectedFilter = this.filterStore.getState('selectedFilter') || [];

      if (selectedFilter.length > 0) {
        const btnClearAll = this.createButtonClearOrClearAll('clearAll');
        const existingButton = contextDocument?.querySelector(FILTER_TREE_MOBILE_BUTTON_CLEAR);

        if (!existingButton) {
          const toolBarMobile = contextDocument?.querySelector(FILTER_TREE_MOBILE_TOOL_BAR);
          toolBarMobile?.append(btnClearAll);
        } else {
          existingButton.replaceWith(btnClearAll);
        }
      }

      // Set level1 to show button clear All
      this.filterStore.setState({
        filterOptionIdMobileSelected: { level: 1 },
      });
    }

    // Handle style2 specific button visibility
    if (filterTreeMobileStyle === 'style2') {
      const backButton = contextDocument?.querySelector(
        FILTER_MOBILE_BUTTON_BACK_CLASS
      ) as HTMLElement | null;
      backButton?.classList?.add(FILTER_MOBILE_BUTTON_HIDDEN);
    }

    // Update button classes
    const backButton = contextDocument?.querySelector(
      FILTER_MOBILE_BUTTON_BACK_CLASS
    ) as HTMLElement | null;
    backButton?.classList?.add(FILTER_MOBILE_BUTTON_CLOSE);
    backButton?.classList?.remove(FILTER_MOBILE_BUTTON_BACK);

    // Remove hidden class from filter option labels
    const mobileWrapper = contextDocument?.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR);
    const filterOptionLabels = mobileWrapper?.querySelectorAll<HTMLElement>(
      '.boost-sd__filter-option-label'
    );
    filterOptionLabels?.forEach((targetElement) =>
      targetElement?.classList?.remove('boost-sd__filter-option-label--hidden')
    );

    // Reset full-height filter options
    const fullHeightElements = mobileWrapper?.querySelectorAll<HTMLElement>(
      FILTER_MOBILE_FULL_HEIGHT_SELECTOR
    );
    fullHeightElements?.forEach((targetElement) => {
      targetElement?.classList?.remove(FILTER_MOBILE_FULL_HEIGHT_CLASS);
      targetElement?.classList?.add(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);

      const buttonFilterOptionToggle = targetElement
        ?.closest('.boost-sd__filter-option')
        ?.querySelector('.boost-sd__filter-option-title') as HTMLElement | null;

      this.toggleAriaExpanded(buttonFilterOptionToggle);
    });
  }

  /**
   * Toggles aria-expanded attribute on an element
   */
  toggleAriaExpanded(element: HTMLElement | null): void {
    if (element) {
      const isExpanded = element.getAttribute('aria-expanded') === 'true';
      element.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
    }
  }

  /**
   * Lazy loads the filter tree to improve initial page load performance.
   *
   * Implements progressive rendering by initially loading a limited number of filter
   * options (LIMIT_FIRST_LOAD), then rendering remaining options asynchronously.
   * This prevents blocking the main thread during filter initialization.
   *
   * @remarks
   * Uses requestIdleCallback for non-blocking rendering of additional options.
   * This method is automatically called during filter initialization to optimize
   * performance on pages with many filter options.
   *
   * Performance impact: Reduces initial render time by ~50% on filter-heavy pages.
   *
   * @example
   * Override to customize lazy loading behavior:
   * ```typescript
   * async lazyLoadFilterTree() {
   *   // Load high-priority filters first
   *   const priorityFilters = this.getPriorityFilters();
   *   await this.renderFilters(priorityFilters);
   *
   *   // Then lazy load the rest
   *   await super.lazyLoadFilterTree();
   * }
   * ```
   */
  async lazyLoadFilterTree(): Promise<void> {
    const filterState = this.filterStore.state.value.filter;
    if (!filterState?.options) {
      console.warn('Filter options not available for lazy loading');
      return;
    }

    let options = filterState.options;

    const {
      showOutOfStockOption,
      showSingleOption: hideSingleOption,
      filterLayout,
      filterTreeHorizontalStyle,
      filterTreeMobileStyle,
    } = this.appService.filterSettings;

    // Update settings option values with settings hideSingle option or show out of stock
    const settingOptionValues = {
      showOutOfStockOption,
      hideSingleOption,
    };

    options = this.filterService.updateValuesOptions(options, settingOptionValues);

    // Calculate width of option horizontally when opening
    if (filterLayout === 'horizontal') {
      this.reCalcWidthFilterOptionHorizontal(filterTreeHorizontalStyle, filterLayout);
    }

    // Initialize viewport management for filter tree
    const filterTreeViewPort = (this.filterStore.state.value as any).filterTreeViewPort || {};

    for (const option of options) {
      if (
        option.values &&
        option.displayType &&
        option.filterOptionId &&
        this.isFilterOptionSupported(option.displayType)
      ) {
        filterTreeViewPort[option.filterOptionId] = {
          loaded: 0,
          total: option.values.length,
          displayType: option.displayType,
        };
      }
    }

    (this.filterStore.state.value as any).filterTreeViewPort = filterTreeViewPort;

    const renderPromises: Promise<void>[] = [];

    if (Object.keys(filterTreeViewPort).length > 0) {
      const isMobileStyle =
        ['style2', 'style2-fullwidth', 'style3', 'style3-fullwidth'].includes(
          filterTreeMobileStyle
        ) && this.filterHelper.isMobile();

      const filterTreeParent = this.filterService.getFilterTreeParent();
      const contextDocument = this.filterStore.state.value.document as HTMLElement;

      for (const [key, value] of Object.entries(filterTreeViewPort)) {
        if ((value as any).loaded !== 0) continue;

        const keyEncodeMetaField = key.replace(/::/g, '__');
        const isTabletPortrait = window.matchMedia('(max-width: 1024px)').matches;
        const optionDomPrefix = isTabletPortrait
          ? 'boost-sd_filter-tree-mobile'
          : 'boost-sd-filter-tree-column';

        const optionDomList = contextDocument.querySelectorAll<HTMLElement>(
          `#${optionDomPrefix} #${keyEncodeMetaField}`
        );

        if (!optionDomList.length) continue;

        const filterOptionWrapperDoms = Array.from(optionDomList).map((dom) =>
          dom.closest('.boost-sd__filter-option')
        );

        const option = options.find((opt) => opt.filterOptionId === key);

        if (filterOptionWrapperDoms && option) {
          const promises = Array.from(filterOptionWrapperDoms).map(async (target) => {
            const targetDom = target?.querySelector<HTMLElement>(`#${keyEncodeMetaField}`);
            if (!targetDom) return;

            if (this.isFilterOptionSupported((value as any).displayType)) {
              const valuesLength = option.values.length;
              const displayAllValues = option.showMoreType === SHOW_MORE_TYPES.DISPLAY_ALL_VALUES;

              // Display first load - support display all values
              const LIMIT_FIRST_LOAD = 30; // ✅ Use correct value
              const firstLoad =
                valuesLength < LIMIT_FIRST_LOAD || displayAllValues
                  ? valuesLength
                  : LIMIT_FIRST_LOAD;

              // Display all values remove class scrollbar
              if (
                displayAllValues &&
                (this.filterHelper.isMobile() ||
                  (!this.filterHelper.isMobile() && filterLayout === 'vertical'))
              ) {
                targetDom
                  .closest('.boost-sd__filter-option-content-inner')
                  ?.classList?.remove('boost-sd__filter-option-content-inner--scroll-bar');
              }

              filterTreeViewPort[key] = {
                loaded: firstLoad,
                total: valuesLength,
                displayType: option.displayType,
              };

              this.filterStore.state.value.filterTreeViewPort = filterTreeViewPort;

              if (isMobileStyle) {
                this.bindingToolbarFilterMobileStyle(option);
              }

              // ✅ Always bind, like original
              this.bindEventSearchOption(option, targetDom, filterTreeParent as HTMLElement);

              if (valuesLength > firstLoad) {
                this.renderLazyFilterOption(targetDom, option);
              }

              await this.renderFilterOption(targetDom, option, firstLoad);
            }
          });

          renderPromises.push(...promises); // ✅ Spread all promises
        }
      }
    }

    requestAnimationFrame(async () => {
      await Promise.all(renderPromises);

      const oldDOM = this.filterStore.state.value.oldDOM as HTMLElement | null;
      if (oldDOM) {
        oldDOM.replaceWith(this.filterStore.state.value.document);
        this.filterStore.state.value.oldDOM = null;
        this.filterHelper.emit(EVENT_NAMES.AFTER_DOCUMENT_CHANGED);
      } else {
        this.filterHelper.emit(EVENT_NAMES.NEW_DOCUMENT_CHANGED);
      }
    });
  }

  /**
   * Recalculates width for horizontal filter options
   * Adapts position left or right based on viewport
   */
  protected reCalcWidthFilterOptionHorizontal(
    filterTreeHorizontalStyle: string,
    filterLayout: string
  ): void {
    if (['style1', 'style2'].includes(filterTreeHorizontalStyle) && filterLayout === 'horizontal') {
      const contextDocument = this.filterStore.state.value.document as HTMLElement;
      const filterOptions = contextDocument?.querySelectorAll<HTMLElement>(
        `#boost-sd__filter-tree-wrapper .boost-sd__filter-option--${filterTreeHorizontalStyle}.boost-sd__filter-option--horizontal`
      );

      if (filterOptions?.length > 0) {
        const WIDTH_WRAPPER_OPTION_STYLE1_HORIZONTAL = 240; // px
        const WIDTH_WRAPPER_OPTION_STYLE2_HORIZONTAL = 532; // px

        const widthFilterOptionStyle =
          filterTreeHorizontalStyle === 'style1'
            ? WIDTH_WRAPPER_OPTION_STYLE1_HORIZONTAL
            : WIDTH_WRAPPER_OPTION_STYLE2_HORIZONTAL;

        filterOptions?.forEach((filterOption) => {
          const currentFilterOption = filterOption?.getBoundingClientRect();

          const wrapper = filterOption?.querySelector<HTMLElement>(
            `.boost-sd__filter-option-itemwrapper--${filterTreeHorizontalStyle}`
          );
          if (
            window.innerWidth / 2 < currentFilterOption?.left &&
            window.innerWidth - currentFilterOption?.left < widthFilterOptionStyle
          ) {
            filterOption?.setAttribute('style', 'position: relative');
            wrapper?.setAttribute('style', 'right: 0');
          }
        });
      }
    }
  }

  /**
   * Checks if display type is supported
   */
  protected isFilterOptionSupported(displayType: string): boolean {
    return ['list', 'box', 'swatch', 'range', 'rating', 'multi_level_collections'].includes(
      displayType
    );
  }

  /**
   * Binds toolbar for mobile filter style
   * Updates title and clear button
   */
  protected bindingToolbarFilterMobileStyle(option: any): void {
    const filterOptionIdMobileSelected = (this.filterStore.state.value as any)
      .filterOptionIdMobileSelected;
    const level = filterOptionIdMobileSelected?.level;
    const currentFilterOptionIdMobile = filterOptionIdMobileSelected?.key;

    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const FILTER_MOBILE_WRAPPER_SELECTOR = '#boost-sd_filter-tree-mobile';
    const mobileWrapper = contextDocument?.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR);

    // Mobile style2 then binding title of toolbar filter selected
    if (currentFilterOptionIdMobile === option.filterOptionId) {
      const refineByTitle = mobileWrapper?.querySelector<HTMLElement>(
        `.boost-sd__filter-tree-vertical-toolbar-middle`
      );

      if (refineByTitle) {
        refineByTitle.innerHTML = option.label;
      }
    }

    // Bind clearAll button to FilterTree Mobile
    const selectedFilters = this.filterStore.state.value.selectedFilter || [];
    let buttonClear: HTMLButtonElement | null = null;

    if (level === 1 || !level) {
      buttonClear = this.createButtonClearOrClearAll('clearAll');
    } else if (level === 2 && currentFilterOptionIdMobile) {
      const hasFilteringCurrentOptionId = selectedFilters.find(
        (filter: any) => filter.metaData?.key === currentFilterOptionIdMobile
      );

      if (hasFilteringCurrentOptionId) {
        buttonClear = this.createButtonClearOrClearAll('clear', currentFilterOptionIdMobile);
      }
    }

    if (buttonClear) {
      if (selectedFilters.length === 0) {
        buttonClear.classList.add('boost-sd__g-hide');
      }

      const existingButton = contextDocument?.querySelector(FILTER_TREE_MOBILE_BUTTON_CLEAR);
      if (!existingButton) {
        const toolBarMobile = contextDocument?.querySelector(FILTER_TREE_MOBILE_TOOL_BAR);
        toolBarMobile?.append(buttonClear);
      } else {
        existingButton.replaceWith(buttonClear);
      }
    }
  }

  /**
   * Creates a clear or clear-all button element for the mobile filter toolbar.
   *
   * Generates a `<button>` pre-configured with the correct action metadata for
   * either clearing a single filter option or all active filters.
   *
   * @param type - Button type: `'clearAll'` removes all filters, `'clear'` removes one option (default: `'clearAll'`)
   * @param optionId - Required when `type` is `'clear'`; the filter option ID to clear
   * @returns A configured `HTMLButtonElement` ready to append to the toolbar
   *
   * @example
   * Override to customize button appearance:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
   *   return class extends FilterRender {
   *     createButtonClearOrClearAll(type, optionId) {
   *       const btn = super.createButtonClearOrClearAll(type, optionId);
   *       btn.classList.add('shop-custom-clear-btn');
   *       return btn;
   *     }
   *   };
   * });
   * ```
   */
  createButtonClearOrClearAll(
    type: 'clearAll' | 'clear' = 'clearAll',
    optionId?: string
  ): HTMLButtonElement {
    const buttonClear = document.createElement('button');
    buttonClear.type = 'button';
    buttonClear.id = 'boost-sd__button-toolbar-mobile-clear';
    buttonClear.className = 'boost-sd__button boost-sd__button--clear';

    if (type === 'clear' && optionId) {
      this.filterTranslation.translate(buttonClear, 'clear', 'Clear');
      buttonClear.setAttribute(
        'data-metadata',
        `{ "action": { "resetFilterOption": { "key": "${optionId}" }}}`
      );
    } else {
      this.filterTranslation.translate(buttonClear, 'clearAll', 'Clear All');
      buttonClear.setAttribute('data-metadata', '{ "action": { "clearAllFilter": "" }}');
    }

    return buttonClear;
  }

  /**
   * Binds search event for filter option
   */
  protected bindEventSearchOption(
    option: any,
    optionDom: HTMLElement,
    filterTreeParent: HTMLElement
  ): void {
    const filterOptionIdEncoded = option.filterOptionId.replace(/::/g, '__');
    const searchInput = filterTreeParent?.querySelector<HTMLInputElement>(
      `#boost-sd__search-option-${filterOptionIdEncoded}`
    );

    if (searchInput) {
      const handleSearch = this.filterHelper.debounce((e: Event) => {
        const optionCopy = JSON.parse(JSON.stringify(option));
        const value = (e.target as HTMLInputElement).value?.toLowerCase();

        // Special case -> collection key = displayName
        let key = 'key';
        if (option.filterType === 'collection') {
          key = 'displayName';
        }

        optionCopy.values = optionCopy.values.filter((item: any) =>
          item[key]?.toString()?.toLowerCase()?.includes(value)
        );

        this.renderFilterOption(optionDom, optionCopy, option.values?.length);
      }, 200);

      searchInput.addEventListener('input', handleSearch);
    }
  }

  /**
   * Renders filter option based on display type
   */
  protected async renderFilterOption(dom: HTMLElement, option: any, limit?: number): Promise<void> {
    const { displayType, filterType } = option;

    // Handle multi-level collections
    if (displayType === 'multi_level_collections') {
      return this.renderOptionMultiLevelCollection({ option, dom, limit });
    }

    // Handle list with multi-level tags
    if (displayType === 'list' && filterType === 'multi_level_tag') {
      return this.renderOptionMultiLevelTag({ option, dom, limit });
    }

    // Handle standard list
    if (displayType === 'list') {
      return this.renderOptionList({ option, dom, limit });
    }

    // Handle box
    if (displayType === 'box') {
      return this.renderOptionBox({ option, dom, limit });
    }

    // Handle swatch
    if (displayType === 'swatch') {
      return this.renderOptionSwatch({ option, dom, limit });
    }

    // Handle rating
    if (displayType === 'rating') {
      return this.renderOptionRating({ option, dom, limit });
    }

    // Handle range
    if (displayType === 'range') {
      return this.renderOptionRangeSlider({ option, dom });
    }

    // Unsupported display type
    console.log('renderFilterOption not covered', option);
  }

  /**
   * Sets up lazy loading for filter option
   */
  protected renderLazyFilterOption(dom: HTMLElement, option: any): void {
    const viewportId = option.filterOptionId;

    switch (true) {
      case option.displayType === 'multi_level_collections':
        this.renderOptionMultiLevelCollection({ option, dom, lazyLoad: true, viewportId });
        break;

      case option.displayType === 'list' && option.filterType === 'multi_level_tag':
        this.renderOptionMultiLevelTag({ option, dom, lazyLoad: true, viewportId });
        break;

      case option.displayType === 'list' && option.filterType !== 'multi_level_tag':
        this.renderOptionList({ option, dom, lazyLoad: true, viewportId });
        break;

      case option.displayType === 'box':
        this.renderOptionBox({ option, dom, lazyLoad: true, viewportId });
        break;

      case option.displayType === 'swatch':
        this.renderOptionSwatch({ option, dom, lazyLoad: true, viewportId });
        break;

      case option.displayType === 'rating':
        this.renderOptionRating({ option, dom, lazyLoad: true, viewportId });
        break;

      case option.displayType === 'range':
        // Range doesn't need lazy loading
        break;

      default:
        console.log('renderLazyFilterOption not covered', option.displayType);
        break;
    }
  }

  /**
   * Renders a complete filter option with all its values and controls.
   *
   * Main rendering method that orchestrates the display of a single filter option,
   * including its title, values, search box (if enabled), and view more controls.
   * Routes to specialized rendering methods based on display type.
   *
   * @param dom - Container DOM element for the filter option
   * @param option - Complete filter option configuration object
   *
   * @remarks
   * This method handles all filter display types: list, box, swatch, rating,
   * range, multi-level collections, and multi-level tags. It also manages
   * collapse state and view more functionality.
   *
   * @example
   * Override to add custom filter option decoration:
   * ```typescript
   * async renderFullFilterOption(dom, option) {
   *   await super.renderFullFilterOption(dom, option);
   *
   *   // Add "Popular" badge to certain filters
   *   if (this.isPopularFilter(option.filterType)) {
   *     const title = dom.querySelector('.boost-sd__filter-option-title');
   *     title?.insertAdjacentHTML('beforeend',
   *       '<span class="badge">Popular</span>'
   *     );
   *   }
   * }
   * ```
   */
  async renderFullFilterOption(dom: HTMLElement, option: any): Promise<void> {
    const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort || {};
    const viewportId = option.filterOptionId;

    // Check if already rendered
    if (filterTreeViewPort[viewportId]?.isRendered) {
      return;
    }

    try {
      switch (option.displayType) {
        case 'multi_level_collections':
          await this.renderOptionMultiLevelCollection({
            option,
            dom,
            viewportId,
            lazyLoad: false,
          });
          break;

        case 'list':
          if (option.filterType === 'multi_level_tag') {
            await this.renderOptionMultiLevelTag({
              option,
              dom,
              viewportId,
              lazyLoad: false,
            });
          } else {
            await this.renderOptionList({
              option,
              dom,
              viewportId,
              lazyLoad: false,
            });
          }
          break;

        case 'box':
          await this.renderOptionBox({
            option,
            dom,
            viewportId,
            lazyLoad: false,
          });
          break;

        case 'swatch':
          await this.renderOptionSwatch({
            option,
            dom,
            viewportId,
            lazyLoad: false,
          });
          break;

        case 'rating':
          await this.renderOptionRating({
            option,
            dom,
            viewportId,
            lazyLoad: false,
          });
          break;

        case 'range':
          await this.renderOptionRangeSlider({
            option,
            dom,
          });
          break;

        default:
          console.warn(`renderFullFilterOption: Unsupported display type "${option.displayType}"`);
          break;
      }

      // Update viewport state to mark as rendered
      if (viewportId && filterTreeViewPort[viewportId]) {
        filterTreeViewPort[viewportId] = {
          ...filterTreeViewPort[viewportId],
          isRendered: true,
          loaded: option.values?.length || 0,
          total: option.values?.length || 0,
        };

        this.filterStore.setState({ filterTreeViewPort });
      }
    } catch (error) {
      console.error(`Error rendering full filter option for ${option.displayType}:`, error);
    }
  }

  // ============================================
  // Refine By Methods
  // ============================================

  /**
   * Renders the "Refine By" section showing active filter selections.
   *
   * Displays all currently selected filters as removable tags, allowing users
   * to see and clear their active selections. Includes a "Clear All" button
   * when multiple filters are selected.
   *
   * @remarks
   * The refine-by section appears in both desktop and mobile views, with
   * different DOM locations. This method updates both simultaneously.
   * Override to customize the display of selected filters.
   *
   * @example
   * Override to add custom refine-by styling:
   * ```typescript
   * async renderRefineBy() {
   *   await super.renderRefineBy();
   *
   *   // Group selected filters by type
   *   const refineBy = document.querySelector('.boost-sd__refine-by');
   *   if (refineBy) {
   *     const tags = refineBy.querySelectorAll('.boost-sd__refine-by-item');
   *     this.groupFilterTags(tags);
   *   }
   * }
   * ```
   */
  async renderRefineBy(): Promise<void> {
    const { filterLayout, showRefineBy } = this.appService.filterSettings;
    const swatchSettings = this.appService.filterSettings?.swatch_settings;

    if (!showRefineBy) return;

    const selectedFilters = this.filterStore.getState('selectedFilter') || [];

    if (selectedFilters && selectedFilters.length > 0) {
      const data = {
        refineBy: {
          title: 'REFINE BY',
          options: selectedFilters,
        },
      };

      // Apply translations
      // this.filterTranslation.applyTranslationsToRefineBy(selectedFilters);

      // Update value display from swatch settings
      if (swatchSettings && Object.keys(swatchSettings).length > 0) {
        this.updateValueDisplayFromSwatch(data, swatchSettings);
      }

      // Render based on layout
      if (filterLayout === 'horizontal') {
        await this.renderRefineByHorizontal(data);
      } else {
        await this.renderRefineByVertical(data);
      }

      await this.renderRefineByMobile(data);
    }
  }

  /**
   * Renders "Refine By" section for mobile layout
   */
  protected async renderRefineByMobile(data: any): Promise<void> {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const doc = contextDocument?.querySelector('#bc-refine-by-mobile');
    let domRefineBy = '';

    const refineBys = contextDocument?.querySelectorAll('.boost-sd__custom-refine-by-mobile');

    if (data && (refineBys?.length || doc)) {
      const template = this.filterHelper.getTemplate('refineByTemplate');
      domRefineBy = await this.appService.templateRender(template, data);
    }

    refineBys?.forEach((refineBy) => {
      refineBy.innerHTML = domRefineBy;
    });

    // Mobile style1
    if (doc) {
      doc.innerHTML = domRefineBy;
    } else {
      // Mobile other styles - bind to filter option selected items
      const selectedByKey: Record<string, string[]> = {};

      data?.refineBy?.options?.forEach((select: any) => {
        if (!select.metaData) return;

        const key = select.metaData.key;
        if (key && select.metaData.value) {
          let value = select.data?.valueDisplay || select.data?.value || select.metaData?.value;
          const isLowerValid = value?.lower != null;
          const isUpperValid = value?.upper != null;

          if (select?.type === 'range' && isLowerValid && isUpperValid) {
            const { lower, upper } = value;
            const numberLower = Number(lower);
            const numberUpper = Number(upper);

            if (!isNaN(numberLower) && !isNaN(numberUpper)) {
              const format = select.data?.moneyFormatValue || '{{amount}}';
              value = `${this.filterHelper.formatMoney(numberLower, format, false)} - ${this.filterHelper.formatMoney(
                numberUpper,
                format,
                false
              )}`;
            }
          }

          if (!selectedByKey[key]) {
            selectedByKey[key] = [value];
          } else {
            selectedByKey[key] = [...selectedByKey[key], value];
          }
        }
      });

      Object.keys(selectedByKey)?.forEach((optionId) => {
        const mobileItemsSelected = contextDocument?.querySelector(
          `${FILTER_MOBILE_WRAPPER_SELECTOR} #${optionId?.replace(
            /::/g,
            '__'
          )}-toggle-target-label .boost-sd__filter-option-mobile-items-selected`
        );

        if (mobileItemsSelected) {
          mobileItemsSelected.innerHTML = selectedByKey[optionId]?.toString();
        }
      });
    }
  }

  /**
   * Renders "Refine By" section for vertical layout
   */
  protected async renderRefineByVertical(data: any): Promise<void> {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const doc = contextDocument?.querySelector('#bc-refine-by');

    if (!doc) return;

    if (data) {
      const template = this.filterHelper.getTemplate('refineByTemplate');

      doc.innerHTML = await this.appService.templateRender(template, data);
    } else {
      doc.innerHTML = '';
    }
  }

  /**
   * Renders "Refine By" section for horizontal layout
   */
  protected async renderRefineByHorizontal(data: any): Promise<void> {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    const doc = contextDocument?.querySelector('#boost-sd__refine-by-horizontal-place');

    if (!doc) return;

    if (data) {
      const template = this.filterHelper.getTemplate('refineByHorizontalTemplate');
      doc.innerHTML = await this.appService.templateRender(template, data);
    } else {
      doc.innerHTML = '';
    }
  }

  /**
   * Updates value display from swatch settings
   * Replaces raw values with configured swatch text
   */
  protected updateValueDisplayFromSwatch(data: any, swatchSettings: any): void {
    if (!data?.refineBy?.options || !Array.isArray(data.refineBy.options)) return;

    data.refineBy.options.forEach((option: any) => {
      if (option.type !== 'swatch') return;

      const rawValue = option.data?.value?.trim?.().toLowerCase?.();
      if (!rawValue) return;

      const swatchObj = swatchSettings[rawValue] || swatchSettings[`${SWATCH_PREFIX}${rawValue}`];
      // Only update valueDisplay if swatch type is "text" (custom text label)
      // Don't update for "one_color", "two_color", "image" types (show original value)
      if (swatchObj?.text && swatchObj?.type === 'text') {
        option.data.valueDisplay = swatchObj.text;
      }
    });
  }

  /**
   * Clears one or more filters from the active filter state
   * Removes filters from the filter tree, updates URL, and triggers API call if needed
   *
   * @param action - Filter action containing the filter to clear and optional dependencies
   */
  clearFilter(action: {
    key: string;
    value: string;
    type?: string;
    displayType?: string;
    dependencies?: Array<{ key: string; value: string; type?: string; displayType?: string }>;
  }): void {
    const { isShortenUrlParam, stickyFilterOnDesktop, filterTreeHorizontalStyle } =
      this.appService.filterSettings;

    // Get all filters to remove (including dependencies)
    const filters = action.dependencies ? [action, ...action.dependencies] : [action];

    // Get current filter state
    const currentFilters = this.filterStore.getState('selectedFilter') || [];
    let newFilterTree = Array.isArray(currentFilters) ? [...currentFilters] : [];

    // Announce filter removal to screen readers
    filters.forEach((filter) => {
      const { key, value } = filter;
      // Find the filter being removed to get its label and display value
      const filterToRemove = currentFilters.find(
        (ft: any) => ft?.metaData?.key === key && ft?.metaData?.value === value
      );

      if (filterToRemove) {
        const filterLabel = filterToRemove.data?.label || key;
        let filterValue = filterToRemove.data?.valueDisplay || value;

        // Format range values for announcement
        if (filterToRemove.type === 'range' && typeof filterToRemove.data?.value === 'object') {
          const rangeValue = filterToRemove.data.value;
          if (rangeValue.lower != null && rangeValue.upper != null) {
            filterValue = `${rangeValue.lower} to ${rangeValue.upper}`;
          }
        }

        this.domHelper.announceToScreenReader(
          `Cleared ${filterLabel}: ${filterValue}`,
          'assertive'
        );
      }
    });

    // Remove each filter from the tree
    filters.forEach((filter) => {
      const { key, value, type, displayType } = filter;
      newFilterTree = newFilterTree.filter(
        (ft: { metaData: { key: string; value: string } }) =>
          ft &&
          (ft.metaData.key !== key || (ft.metaData.key === key && ft.metaData.value !== value))
      );
      this.removeFilterSelectedClass(key, value, type || displayType || '');
    });

    // Update URL history
    this.filterHelper.removeQueryParamsHistory(filters, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });

    // Update filter state
    this.filterStore.setState({ selectedFilter: newFilterTree });

    // Set flag to indicate this is a clear action (not a filter selection)
    this.filterStore.setState({ isClearing: true });

    this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);

    // Scroll to top if needed for desktop horizontal expand style with sticky enabled
    if (filterTreeHorizontalStyle === 'style-expand' && stickyFilterOnDesktop) {
      setTimeout(() => {
        this.domHelper.scrollToTop({
          isFilter: true,
          scrollToElement: '.boost-sd-container',
        });
      }, 0);
    }
  }

  /**
   * Applies filter option changes
   * Handles scrolling, closing horizontal options, and resetting filter state
   *
   * @param action - Filter action ('applyAll' or filter option key)
   */
  applyFilterOption(action: string | { key?: string; from?: string }): void {
    const actionValue = typeof action === 'string' ? action : action.key;

    if (actionValue === 'applyAll') {
      // Force close filter tree for horizontal expand layout
      const buttonToggle = document.querySelector(
        '.boost-sd__filter-tree-toggle-button.boost-sd__filter-tree-toggle-button--expand'
      );
      (buttonToggle as HTMLElement)?.click();
    }

    const scrollOptions = this.determineScrollTarget();
    this.domHelper.scrollToTop(scrollOptions);

    this.closeFilterOptionHorizontalOpening();
    this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);

    // Reset selecting state when applying
    this.filterStore.setState({ filterOptionItemSelecting: {} });
  }

  /**
   * Closes all horizontally opened filter options
   * Updates collapse state to close any open options
   */
  closeFilterOptionHorizontalOpening(): void {
    const collapseState =
      (this.filterStore.state.value[COLLAPSE_STATE] as Record<string, any>) || {};
    const updatedCollapseState = { ...collapseState };

    Object.keys(updatedCollapseState).forEach((key) => {
      const targets = updatedCollapseState[key]?.targets || [];
      if (
        targets &&
        targets.length > 0 &&
        !targets[0].shouldAddClass &&
        targets[1]?.shouldAddClass
      ) {
        updatedCollapseState[key] = this.getFilterOptionToggleStateForClose(
          updatedCollapseState[key]
        );
      }
    });

    this.filterStore.setState({ [COLLAPSE_STATE]: updatedCollapseState });
  }

  /**
   * Gets toggled state for closing a filter option
   * Helper method for closeFilterOptionHorizontalOpening
   */
  protected getFilterOptionToggleStateForClose(currentAction: {
    actionId?: string;
    filterLayout?: string;
    targets?: Array<{
      targetId: string;
      classToggle: string;
      shouldAddClass: boolean;
    }>;
  }): typeof currentAction {
    const document = this.filterStore.getState('document') as HTMLElement;
    const filterTreeParent = document.querySelector('.boost-sd-left, #boost-sd_filter-tree-mobile');
    let targets = currentAction?.targets || [];

    if (targets.length > 0) {
      targets = targets.map((target) => {
        const targetElement = filterTreeParent?.querySelector(
          `#${target.targetId}`
        ) as HTMLElement | null;
        if (targetElement) {
          const buttonFilterOptionToggle = targetElement.querySelector(
            '.boost-sd__filter-option-title'
          ) as HTMLElement | null;
          this.toggleAriaExpanded(buttonFilterOptionToggle);

          return {
            ...target,
            shouldAddClass: !target.shouldAddClass,
          };
        }

        return target;
      });

      currentAction.targets = targets;
    }

    return currentAction;
  }

  /**
   * Determines the appropriate scroll target based on layout and sticky settings
   * Returns scroll options for the current filter configuration
   */
  protected determineScrollTarget(): {
    isFilter: boolean;
    scrollToElement?: string;
    behavior?: 'smooth' | 'auto';
  } {
    const { filterLayout, stickyFilterOnDesktop, filterTreeHorizontalStyle, stickyFilterOnMobile } =
      this.appService.filterSettings;

    const isMobileWidth = this.filterHelper.isMobile();

    if (stickyFilterOnMobile && isMobileWidth) {
      return {
        isFilter: true,
        scrollToElement: '.boost-sd__toolbar-container',
      };
    }

    if (stickyFilterOnDesktop && !isMobileWidth && filterLayout === 'horizontal') {
      if (filterTreeHorizontalStyle !== 'style-expand') {
        const refineByElement = document.querySelector('div[class^="boost-sd__refine-by"]');
        const havingRefineBy = refineByElement && refineByElement.textContent.trim().length > 0;
        if (havingRefineBy) {
          return { isFilter: true, scrollToElement: 'div[class^="boost-sd__refine-by"]' };
        } else {
          return { isFilter: true };
        }
      } else {
        return { isFilter: true, scrollToElement: '.boost-sd-container' };
      }
    } else {
      const refineByElement = document.querySelector('#bc-refine-by-mobile');
      const havingRefineBy = refineByElement && refineByElement.textContent.trim().length > 0;
      if (havingRefineBy) {
        return { isFilter: true, scrollToElement: '#bc-refine-by-mobile' };
      } else {
        return { isFilter: true, behavior: 'auto' };
      }
    }
  }

  /**
   * Resets a specific filter option
   * Removes the filter from state, updates URL params, and triggers API refresh
   *
   * @param action - Filter action containing the key to reset
   */
  resetFilterOption(action: { key: string; from?: string }): void {
    const { key } = action;
    const filterState = this.filterStore.state.value.filter;
    const options = filterState?.options || [];
    const { isShortenUrlParam } = this.appService.filterSettings;

    // If resetting collection filter, also reset tag filter
    const collectionFilterOptionId = this.filterService.getCollectionFilterOptionId(options);
    const keys = key === collectionFilterOptionId ? [key, FILTER_TAG_KEY] : [key];

    // Get current selected filters
    const selectedFilters = (this.filterStore.getState('selectedFilter') as any[]) || [];
    const newFilterTree = selectedFilters.filter((ft: any) => ft && !keys.includes(ft.data?.key));

    // Update state
    this.filterStore.setState({ selectedFilter: newFilterTree });

    // Close any open horizontal filter options
    this.closeFilterOptionHorizontalOpening();

    // Remove query params from URL
    this.filterHelper.deleteQueryParamsHistory(keys, {
      preventPushHistory: false,
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });

    // Reset and trigger new filter request
    this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);
  }

  /**
   * Handles list filter option selection
   * Manages single/multiple selection, URL params, and triggers filter request
   *
   * @param action - Filter action containing key, value, filterType, selectType, displayType
   */
  /**
   * Handles filter option list selection/deselection.
   *
   * Main handler for list-style filter option clicks. Manages both single-select
   * and multi-select filter types, updating state, URL parameters, and triggering
   * API calls to refresh product results.
   *
   * @param action - Filter option selection action
   * @param action.key - Filter parameter key (e.g., 'pf_st_color')
   * @param action.value - Selected value (e.g., 'red')
   * @param action.filterType - Type of filter (vendor, tag, etc.)
   * @param action.selectType - Selection mode ('single' or 'multiple')
   * @param action.displayType - Display format (list/box/swatch)
   * @param action.valueDisplay - Display label for the value
   * @param action.label - Filter option label
   *
   * @remarks
   * This method coordinates multiple operations:
   * - Updates selectedFilter state
   * - Modifies URL parameters
   * - Triggers FILTER_RESET event for API call
   * - Updates UI to show selection
   *
   * @example
   * Override to add custom filter tracking:
   * ```typescript
   * async optionList(action) {
   *   // Track filter selection
   *   this.analytics.track('filter_selected', {
   *     filterType: action.filterType,
   *     value: action.value,
   *     selectType: action.selectType
   *   });
   *
   *   return super.optionList(action);
   * }
   * ```
   */
  async optionList(action: {
    key: string;
    value: string;
    filterType?: string;
    selectType?: 'single' | 'multiple';
    displayType?: string;
    label?: string;
    valueDisplay?: string;
    handle?: string;
  }): Promise<void> {
    const { filterLayout, requestInstantly } = this.appService.filterSettings;
    let isCalled = true;

    // If filterType is collection, route to collection handler
    if (action?.filterType === 'collection') {
      return this.handleCollectionOption(action as any);
    }

    // Check if horizontal layout with apply button (deferred request)
    const horizontalHasApplyButton =
      filterLayout === 'horizontal' && !requestInstantly && !this.filterHelper.isMobile();

    if (horizontalHasApplyButton) {
      // Toggle selection state without triggering API call
      const itemSelecting = this.filterStore.getState('filterOptionItemSelecting') || {};

      const itemKey = `${action.key}-${action.value}`;
      if (!itemSelecting[itemKey]) {
        itemSelecting[itemKey] = true;
      } else {
        delete itemSelecting[itemKey];
      }

      this.filterStore.setState({ filterOptionItemSelecting: itemSelecting });
      isCalled = false;
    }

    // Scroll to top if filter is being applied immediately
    if (isCalled) {
      this.domHelper.scrollToTop({ isFilter: true });
    }

    const { key, value, selectType } = action;
    const selectedFilters = this.filterStore.getState('selectedFilter') || [];

    // Detect if this is keyboard navigation
    const isKeyboardNavigation = this.filterHelper.isEnterOrSpaceKeyPressed();

    // Check if this filter value is already selected
    const isAlreadySelected = selectedFilters.some(
      (filter: any) => filter?.data?.key === key && filter?.data?.value === value
    );

    if (isAlreadySelected) {
      // Remove the filter (clear it)
      this.clearFilter(action as any);
    } else {
      // Add or replace the filter
      if (selectType === 'multiple') {
        this.addOptionList(action);
      } else {
        this.setOptionList(action);
      }

      // Announce selection to screen reader for all users (keyboard and mouse)
      const filterLabel = action.label || key;
      const valueLabel = action.valueDisplay || value;
      this.domHelper.announceToScreenReader(`${filterLabel}: ${valueLabel} selected`, 'assertive');
    }

    // Store keyboard state for API call delay
    this.filterStore.setState({ filterKeyboardActive: isKeyboardNavigation });

    // Trigger API call if not deferred
    if (isCalled) {
      this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);
    }

    // Update UI to show selected options
    this.renderSelectedOption();
  }

  /**
   * Adds a filter option to the selected filters (multiple selection)
   * Appends to existing filters and updates URL with new value
   *
   * @param action - Filter action to add
   */
  protected addOptionList(action: {
    key: string;
    value: string;
    displayType?: string;
    [key: string]: any;
  }): void {
    const { key, value, displayType } = action;
    const selectedFilters = this.filterStore.getState('selectedFilter') || [];
    const { isShortenUrlParam } = this.appService.filterSettings;

    // Add selected class to UI
    this.addFilterSelectedClass(key, value, displayType || 'list');

    // Generate filter data and add to tree
    const filterData = this.filterService.generateFilterDataForList(key, value, action);
    const newFilterTree = [...selectedFilters, filterData];

    // Update state
    this.filterStore.setState({ selectedFilter: newFilterTree });

    // Append to URL params
    this.filterHelper.appendQueryParamHistory(key, value, false, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });
  }

  /**
   * Replaces filter option selection (single selection)
   * Removes existing values for the key and sets new value
   *
   * @param action - Filter action to set
   */
  protected setOptionList(action: {
    key: string;
    value: string;
    displayType?: string;
    [key: string]: any;
  }): void {
    const { key, value, displayType } = action;
    const selectedFilters = this.filterStore.getState('selectedFilter') || [];
    const { isShortenUrlParam } = this.appService.filterSettings;

    // Remove selected classes from previous values
    selectedFilters
      .filter((filter: any) => filter.data.key === key)
      .forEach((filter: any) => {
        this.removeFilterSelectedClass(filter.data.key, filter.data.value, displayType || 'list');
      });

    // Remove previous values from filter tree
    const newFilterTree = selectedFilters.filter((filter: any) => filter.data.key !== key);

    // Add selected class and generate new filter data
    this.addFilterSelectedClass(key, value, displayType || 'list');
    const filterData = this.filterService.generateFilterDataForList(key, value, action);
    newFilterTree.push(filterData);

    // Update state
    this.filterStore.setState({ selectedFilter: newFilterTree });

    // Set URL param (replaces existing)
    this.filterHelper.setQueryParamHistory(key, value, false, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });
  }

  /**
   * Toggles classes on target elements
   * Used for expand/collapse functionality
   *
   * @param action - Action containing targets to toggle
   * @param containerId - Optional container selector to scope the query
   */
  protected handleToggle(
    action: {
      targets?: Array<{
        targetId: string;
        classToggle: string;
      }>;
    },
    containerId = ''
  ): void {
    if (!action) return;

    const targets = action.targets;
    if (targets && targets.length > 0) {
      const contextDocument = this.filterStore.state.value.document as HTMLElement;
      targets.forEach((target) => {
        const selector = containerId ? `${containerId} #${target.targetId}` : `#${target.targetId}`;
        const targetElement = contextDocument.querySelector<HTMLElement>(selector);
        targetElement?.classList?.toggle(target.classToggle);
      });
    }
  }

  /**
   * Expands or collapses multi-level filter options
   * Manages state persistence for multi-level filter option expansion
   *
   * @param action - Action containing actionId and targets for expand/collapse
   */
  expandCollapseMultiLevel(action: {
    actionId: string;
    targets?: Array<{
      targetId: string;
      classToggle: string;
      shouldAddClass: boolean;
    }>;
  }): void {
    const { keepToggleState } = this.appService.filterSettings;

    if (keepToggleState) {
      const collapseStateMultipleLevel =
        this.filterStore.getState('collapseStateMultipleLevel') || {};

      // Restore previous state if exists
      if (collapseStateMultipleLevel[action.actionId]) {
        action = collapseStateMultipleLevel[action.actionId];
      }

      // Toggle targets' shouldAddClass property
      if (action?.targets?.length && action.targets.length > 0) {
        action.targets = action.targets.map((target) => ({
          ...target,
          shouldAddClass: !target.shouldAddClass,
        }));
      }

      // Update state
      this.filterStore.setState({
        collapseStateMultipleLevel: {
          ...collapseStateMultipleLevel,
          [action.actionId]: action,
        },
      });
    }

    // Toggle the elements (mobile or desktop)
    const containerId = this.filterHelper.isMobile() ? '#boost-sd_filter-tree-mobile' : '';
    this.handleToggle(action, containerId);
  }

  /**
   * Clears all active filters and resets the filter state.
   *
   * Removes all selected filters from state, clears all URL query parameters,
   * emits `FILTER_RESET` to trigger a new API call, and scrolls to the top.
   *
   * @param options - Configuration options
   * @param options.preventPushHistory - When `true`, skips the URL history push (default: `false`)
   *
   * @example
   * Override to add a confirmation before clearing:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterRender', (FilterRender) => {
   *   return class extends FilterRender {
   *     clearAllFilters(options) {
   *       if (confirm('Clear all filters?')) {
   *         super.clearAllFilters(options);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  clearAllFilters(options: { preventPushHistory?: boolean } = { preventPushHistory: false }) {
    const { isShortenUrlParam } = this.appService.filterSettings;

    this.filterStore.setState({ selectedFilter: [] });

    // Set flag to indicate this is a clear action (not a filter selection)
    this.filterStore.setState({ isClearing: true });

    this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);
    this.filterService.clearAllParamHistory({
      preventPushHistory: options.preventPushHistory,
      isShortenUrlParam,
    });
    this.domHelper.scrollToTop({ isFilter: true });
  }

  // ============================================
  // Common Helper Methods
  // ============================================

  /**
   * Returns a lookup map of currently selected filter value keys.
   *
   * Builds a `Record<string, boolean>` where keys follow the pattern
   * `"filterOptionId-value"` (lowercased), used for O(1) selected-state checks
   * during template rendering.
   *
   * @returns Map of selected filter keys to `true`
   */
  getSelectedKeysFilter(): Record<string, boolean> {
    const selectedFilters = this.filterStore.getState('selectedFilter') || [];
    const selectedKeys: Record<string, boolean> = {};

    if (Array.isArray(selectedFilters)) {
      selectedFilters.forEach((filter: any) => {
        if (filter?.data?.key && filter?.data?.value) {
          selectedKeys[`${filter.data.key}-${filter.data.value}`.toLowerCase()] = true;
        }
      });
    }

    return selectedKeys;
  }

  /**
   * Returns a slice of option values up to the given size limit.
   *
   * @param option - Filter option configuration with a `values` array
   * @param size - Maximum number of values to return
   * @returns Array of at most `size` filter values, or empty array if inputs are invalid
   */
  getLimitOptionValues(option: any, size: number): any[] {
    if (Array.isArray(option.values) && typeof size === 'number' && size > 0) {
      return option.values.slice(0, size);
    }
    return [];
  }

  /**
   * Filters XSS-unsafe values from option values up to the given size.
   *
   * Iterates option values and excludes any whose label, displayName, or key
   * contains potentially malicious content as detected by `FilterValidationHelper`.
   *
   * @param option - Filter option configuration with a `values` array
   * @param size - Maximum number of safe values to return
   * @returns Array of XSS-safe filter values, up to `size` items
   */
  xssFilterOption(option: any, size: number): any[] {
    const values = [...option.values];
    const newValues: any[] = [];

    for (const value of values) {
      if (newValues.length >= size) break;
      const isBad = this.filterValidation.isBadSearchTerm(
        value.label || value.displayName || value.key
      );
      if (!isBad) {
        newValues.push(value);
      }
    }

    return newValues;
  }

  /**
   * Checks if the option uses "view more" pagination.
   *
   * @param option - Filter option configuration object
   * @returns True if the option uses `VIEW_MORE` or `VIEW_MORE_SCROLL_BAR` pagination
   */
  isViewMoreFilterOption(option: any): boolean {
    return (
      !!option.showMoreType &&
      [SHOW_MORE_TYPES.VIEW_MORE, SHOW_MORE_TYPES.VIEW_MORE_SCROLL_BAR].includes(
        option.showMoreType
      )
    );
  }

  /**
   * Gets the number of items to show before the "View More" button.
   *
   * Returns the configured threshold for the view more button, which may be
   * customized globally via `window.boostWidgetIntegrationConfig.startViewMore`.
   *
   * @param option - Filter option configuration with a `displayType` property
   * @param isFilterTreeVertical - Whether the filter tree uses vertical layout
   * @returns Number of items to display before collapsing behind "View More"
   */
  getNumberViewMore(option: any, isFilterTreeVertical: boolean): number {
    const defaultViewMore = isFilterTreeVertical
      ? { list: 5, box: 3, swatch: 10 }
      : { list: 10, box: 20, swatch: 10 };

    const viewMoreConfig =
      (window as any).boostWidgetIntegrationConfig?.startViewMore || defaultViewMore;

    switch (option.displayType) {
      case 'box':
        return viewMoreConfig.box;
      case 'swatch':
        return viewMoreConfig.swatch;
      default:
        return viewMoreConfig.list;
    }
  }

  // ============================================
  // Template Rendering Methods
  // ============================================

  /**
   * Renders filter option values using the list template.
   *
   * Generates HTML for list-style filter options with checkboxes and labels.
   * Supports document count display and selected state management.
   *
   * @param params - List rendering parameters
   * @param params.option - Filter option configuration
   * @param params.values - Array of filter values to render
   * @param params.dom - Container element for the rendered list
   * @param params.label - Filter option label
   * @param params.showDocCount - Whether to show product counts
   * @param params.selectedKeys - Map of selected value keys
   * @param params.classNameSelected - CSS class for selected items
   * @param params.classNameUnselected - CSS class for unselected items
   *
   * @remarks
   * Uses the filterOptionListTemplate from template system. Override to
   * customize list item rendering or add shop-specific elements.
   *
   * @example
   * Override to add custom list item attributes:
   * ```typescript
   * async renderOptionListTemplate(params) {
   *   await super.renderOptionListTemplate(params);
   *   // Add data attributes to list items
   *   params.dom.querySelectorAll('.boost-sd__filter-option-listitem').forEach(item => {
   *     const value = item.getAttribute('data-value');
   *     item.setAttribute('data-popularity', this.getValuePopularity(value));
   *   });
   * }
   * ```
   */
  async renderOptionListTemplate(params: {
    option: any;
    values: any[];
    dom: HTMLElement;
    label: string;
    showDocCount: boolean;
    selectedKeys: Record<string, boolean>;
    classNameSelected: string;
    classNameUnselected: string;
  }): Promise<void> {
    await this.renderOptionTemplate({
      ...params,
      templateType: 'list',
      formatPrices: true,
    });
  }

  /**
   * Renders filter option values using the box/grid template.
   *
   * Generates HTML for box-style filter options displayed in a grid layout.
   * Commonly used for visual filters like size or style options.
   *
   * @param params - Box rendering parameters
   * @param params.option - Filter option configuration
   * @param params.values - Array of filter values to render
   * @param params.dom - Container element for the rendered boxes
   * @param params.label - Filter option label
   * @param params.selectedKeys - Map of selected value keys
   * @param params.classNameSelected - CSS class for selected items
   * @param params.classNameUnselected - CSS class for unselected items
   *
   * @remarks
   * Uses the filterOptionBoxTemplate from template system. Box layout
   * provides larger click targets and better visual hierarchy.
   *
   * @example
   * Override to customize box styling:
   * ```typescript
   * async renderOptionBoxTemplate(params) {
   *   await super.renderOptionBoxTemplate(params);
   *   // Add hover effects to boxes
   *   params.dom.querySelectorAll('.boost-sd__filter-option-box-item').forEach(box => {
   *     box.addEventListener('mouseenter', () => this.showPreview(box));
   *   });
   * }
   * ```
   */
  async renderOptionBoxTemplate(params: {
    option: any;
    values: any[];
    dom: HTMLElement;
    label: string;
    selectedKeys: Record<string, boolean>;
    classNameSelected: string;
    classNameUnselected: string;
  }): Promise<void> {
    await this.renderOptionTemplate({
      ...params,
      templateType: 'box',
      formatPrices: false,
    });
  }

  /**
   * Renders filter option values using the swatch template.
   *
   * Generates HTML for color/image swatch filter options. Supports different
   * swatch shapes (circle/square) and layout types (grid/list).
   *
   * @param params - Swatch rendering parameters
   * @param params.option - Filter option configuration
   * @param params.values - Array of filter values to render
   * @param params.dom - Container element for the rendered swatches
   * @param params.label - Filter option label
   * @param params.swatchShape - Shape of swatches (circle/square)
   * @param params.swatchType - Layout type (grid/list)
   * @param params.showDocCount - Whether to show product counts
   * @param params.selectedKeys - Map of selected value keys
   * @param params.classNameSelected - CSS class for selected items
   * @param params.classNameUnselected - CSS class for unselected items
   *
   * @remarks
   * Swatches can display solid colors, gradients, or images. The template
   * system automatically generates appropriate CSS based on swatch data.
   *
   * @example
   * Override to add swatch tooltips:
   * ```typescript
   * async renderOptionSwatchTemplate(params) {
   *   await super.renderOptionSwatchTemplate(params);
   *   // Add color name tooltips
   *   params.dom.querySelectorAll('.boost-sd__swatch').forEach(swatch => {
   *     const colorName = swatch.getAttribute('data-color-name');
   *     swatch.title = colorName;
   *   });
   * }
   * ```
   */
  async renderOptionSwatchTemplate(params: {
    option: any;
    values: any[];
    dom: HTMLElement;
    label: string;
    swatchShape: string;
    swatchType: string;
    showDocCount: boolean;
    selectedKeys: Record<string, boolean>;
    classNameSelected: string;
    classNameUnselected: string;
  }): Promise<void> {
    await this.renderOptionTemplate({
      ...params,
      templateType: 'swatch',
      formatPrices: false,
    });
  }

  /**
   * Unified option rendering logic
   * Handles list, box, and swatch display types
   */
  protected async renderOptionTemplate(params: {
    option: any;
    values: any[];
    dom: HTMLElement;
    label: string;
    selectedKeys: Record<string, boolean>;
    classNameSelected: string;
    classNameUnselected: string;
    templateType: 'list' | 'box' | 'swatch';
    formatPrices: boolean;
    showDocCount?: boolean;
    swatchShape?: string;
    swatchType?: string;
  }): Promise<void> {
    const {
      option,
      values,
      dom,
      label,
      selectedKeys,
      classNameSelected,
      classNameUnselected,
      templateType,
      formatPrices,
      showDocCount,
      swatchShape,
      swatchType,
    } = params;

    const { precisionFormatLabelPriceList } = this.appService.filterSettings;
    const actionMapping = this.filterStore.getState('actionMapping') || {};

    let html = '';

    for (const value of values) {
      const { key, to, from } = value;
      const fromToKey = formatPrices && (to || from) ? this.formatFromToKey(from, to) : key;
      value.key = fromToKey;
      const actionId = this.filterHelper.simpleMd5(`${option.filterOptionId}-${fromToKey}`);
      const itemKey = `${option.filterOptionId}-${fromToKey}`.toLowerCase();

      const isSelected = selectedKeys[itemKey];
      const className = isSelected ? classNameSelected : classNameUnselected;
      const dataAction = `${option.filterOptionId}.${actionId}`;

      let displayValue = '';
      if (['price', 'variants_price'].includes(option.filterType)) {
        displayValue = this.filterService.formatPriceLabel(to, from, precisionFormatLabelPriceList);
      } else if (option.filterType === 'percent_sale') {
        displayValue = this.filterService.formatPercentSaleLabel(from, to);
      } else {
        displayValue = this.replacePrefixValue(option, value);
      }
      value.label = displayValue;

      actionMapping[option.filterOptionId] = actionMapping[option.filterOptionId] || {};
      actionMapping[option.filterOptionId][actionId] = {
        optionList: {
          key: option.filterOptionId,
          value: value.key,
          label: option.label,
          filterType: option.filterType,
          selectType: option.selectType,
          displayType: option.displayType,
          valueDisplay: value.label || '',
          handle: value.handle || '',
        },
      };

      if (templateType === 'swatch') {
        let className = classNameUnselected;
        let classButton =
          'boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button ';
        if (selectedKeys[`${option.filterOptionId}-${value.key}`?.toString()?.toLowerCase()]) {
          classButton += 'boost-sd__filter-option-item-button--selected';
          className = classNameSelected;
        }
        const swatchValue = this.renderSingleSwatch(value.key);

        const rendered = await this.appService.templateRender(
          this.filterHelper.getTemplate('filterOptionSwatchTemplate'),
          {
            option,
            actionId,
            value,
            label,
            className,
            classButton,
            dataAction,
            swatchValue,
            swatchShape,
            swatchType,
            showDocCount,
          }
        );

        html += rendered;
      } else if (templateType === 'box') {
        html += await this.appService.templateRender(
          this.filterHelper.getTemplate('filterOptionBoxTemplate'),
          {
            actionId,
            dataAction,
            value,
            label,
            className,
            displayAllValuesInUppercaseForm: option.displayAllValuesInUppercaseForm,
          }
        );
      } else {
        // list template
        const htmlRendered = await this.appService.templateRender(
          this.filterHelper.getTemplate('filterOptionListTemplate'),
          {
            actionId,
            dataAction,
            value,
            label,
            className,
            showDocCount: value.handle === 'all' ? false : showDocCount,
            displayAllValuesInUppercaseForm: option.displayAllValuesInUppercaseForm,
          }
        );
        html += htmlRendered;
      }
    }

    const currentActionMapping = this.filterStore.getState('actionMapping') || {};
    this.filterStore.setState({ actionMapping: { ...currentActionMapping, ...actionMapping } });
    dom.innerHTML = html;

    // Emit event after render
    this.filterHelper.emit(EVENT_NAMES.FILTER_OPTION_RENDERED as any);
  }

  /**
   * Formats from-to key for price/percent filters
   */
  protected formatFromToKey(from: number, to: number): string {
    let key = '';
    if (!from) {
      key = `0:${to}`;
    } else if (!to) {
      key = `${from}:`;
    } else {
      key = `${from}:${to}`;
    }
    return key;
  }

  /**
   * Replaces prefix value based on option settings
   */
  protected replacePrefixValue(option: any, value: any): string {
    let replacedLabel = value?.label;

    if (option?.prefix) {
      const prefix = option?.prefix?.replace(/\\/g, '') || '';
      if (!replacedLabel) replacedLabel = value?.key || value?.tag || '';
      replacedLabel = replacedLabel.replace(prefix, '').trim();
    }

    return replacedLabel;
  }

  /**
   * Renders single swatch value with background color/image
   */
  protected renderSingleSwatch(value: string): any {
    let swatchValue = this.buildSwatchSettings(value);

    // If don't have swatch settings, fallback to default colors
    if (!swatchValue?.hasSwatchSetting) {
      swatchValue = {
        backgroundColor: this.buildBackgroundColor(value),
        imageUrl: '',
        hasSwatchSetting: false,
      };
    }

    swatchValue.swatchBorder = ['white', '#FFFFFF'].includes(swatchValue.backgroundColor)
      ? 'has-border'
      : '';
    return swatchValue;
  }

  /**
   * Builds swatch settings from filter settings
   */
  protected buildSwatchSettings(value: string): any {
    const swatchSettings = this.appService.filterSettings?.swatch_settings;
    if (!swatchSettings) return { hasSwatchSetting: false };

    let swatchValue: any = { hasSwatchSetting: false };
    const swatchKeys = Object.keys(swatchSettings);

    if (Array.isArray(swatchKeys) && swatchKeys.length > 0) {
      // Duplicate trimmable items (handle whitespace in keys)
      const trimmedKeys: string[] = [];
      for (let i = 0; i < swatchKeys.length; i++) {
        const key = swatchKeys[i];
        const keyStr = key.toString();
        const trimmedKey = keyStr.trim();
        if (trimmedKey !== keyStr) {
          const swatch = { ...swatchSettings[key], name: trimmedKey };
          swatchSettings[trimmedKey] = swatch;
          trimmedKeys.push(trimmedKey);
        }
      }
      // Add trimmed keys to the array after iteration
      swatchKeys.push(...trimmedKeys);

      // Find matching key with prefix handling
      const swatchSettingKey = swatchKeys.find((key) => {
        const keyWithoutPrefix = key.replace('pfs-swatch-', '');
        return keyWithoutPrefix === value || keyWithoutPrefix.toLowerCase() === value.toLowerCase();
      });

      if (swatchSettingKey) {
        swatchValue = { ...swatchSettings[swatchSettingKey] };
      }
    }

    if (swatchValue && Object.keys(swatchValue).length > 1) {
      const SWATCH_TYPES = {
        ONE_COLOR: 'one_color',
        TWO_COLORS: 'two_colors',
        IMAGE: 'image',
        TEXT: 'text',
      };

      switch (swatchValue.type) {
        case SWATCH_TYPES.ONE_COLOR:
          if (swatchValue.colorCodes?.length > 0 && swatchValue.colorCodes[0]) {
            swatchValue.backgroundColor = swatchValue.colorCodes[0];
            if (swatchValue.imageUrl) {
              swatchValue.backgroundImage = 'url(' + swatchValue.imageUrl + ');';
            } else {
              swatchValue.backgroundImage = 'none';
            }
            swatchValue.hasSwatchSetting = true;
          }
          break;

        case SWATCH_TYPES.TWO_COLORS:
          if (
            swatchValue.colorCodes?.length > 1 &&
            swatchValue.colorCodes[0] &&
            swatchValue.colorCodes[1]
          ) {
            swatchValue.backgroundImage =
              'linear-gradient(to top left, ' +
              swatchValue.colorCodes[1] +
              ' 50%, ' +
              swatchValue.colorCodes[0] +
              ' 50%);';
            swatchValue.backgroundColor = 'transparent';
            swatchValue.hasSwatchSetting = true;
          }
          break;

        case SWATCH_TYPES.IMAGE:
          if (swatchValue.imageUrl) {
            swatchValue.backgroundImage = 'url(' + swatchValue.imageUrl + ');';
            swatchValue.backgroundColor = this.buildBackgroundColor(swatchValue.name); // Fallback color for image
            swatchValue.hasSwatchSetting = true;
          }
          break;

        case SWATCH_TYPES.TEXT:
          if (swatchValue.text) {
            swatchValue.hasSwatchSetting = true;
          }
          break;

        default:
          break;
      }
    }

    return swatchValue;
  }

  /**
   * Builds background color from label (extracts last segment after hyphen)
   */
  protected buildBackgroundColor(label?: string): string {
    if (!label) return '';
    const splitColorArr = label.split('-');
    return splitColorArr[splitColorArr.length - 1];
  }

  // ============================================
  // Box Filter Methods
  // ============================================

  /**
   * Unified renderOptionBox with lazy loading and full render support
   */
  async renderOptionBox(params: {
    option: any;
    dom: HTMLElement;
    limit?: number;
    lazyLoad?: boolean;
    viewportId?: string;
  }): Promise<void> {
    const { option, dom, limit, lazyLoad = false, viewportId } = params;

    if (!dom) return;
    if (option.values?.length === 0) return;

    // Handle lazy loading
    if (lazyLoad) {
      this.setupLazyLoadOptionBox(dom, viewportId || '', option);
      return;
    }

    // Handle full render from viewport
    if (viewportId) {
      const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort || {};
      if (filterTreeViewPort[viewportId]?.isRendered) {
        return;
      }
      filterTreeViewPort[viewportId] = {
        ...filterTreeViewPort[viewportId],
        isRendered: true,
      };
      this.filterStore.state.value.filterTreeViewPort = filterTreeViewPort;
    }

    const selectedKeys = this.getSelectedKeysFilter();
    const classNameUnselected = 'boost-sd__filter-option-item boost-sd__filter-option-box-item';
    const classNameSelected = `${classNameUnselected} boost-sd__filter-option-box-item--selected`;

    const size = limit && option.values.length > limit ? limit : option.values.length;
    const isXssDisabled = (this.appService.TAEAppConfig as any).xss?.filter?.disabled;
    let values = isXssDisabled
      ? this.getLimitOptionValues(option, size)
      : this.xssFilterOption(option, size);

    const label = option.label;

    // Support custom sort values
    if (typeof (window as any).boostCustomSortOptionValues === 'function') {
      values = (window as any).boostCustomSortOptionValues(values, option);
    }

    await this.renderOptionBoxTemplate({
      option,
      values,
      dom,
      label,
      selectedKeys,
      classNameSelected,
      classNameUnselected,
    });
  }

  /**
   * Sets up lazy loading for option box
   */
  protected setupLazyLoadOptionBox(dom: HTMLElement, id: string, option: any): void {
    // Do not lazy load for View More options
    if (this.isViewMoreFilterOption(option)) return;

    const domMouseMove = dom.closest('.boost-sd__filter-option');
    if (!domMouseMove) return;

    const mousemoveRender = this.filterHelper.debounce(() => {
      this.renderOptionBox({
        option,
        dom,
        lazyLoad: false,
        viewportId: id,
      });
    }, 200);

    domMouseMove.addEventListener('mousemove', mousemoveRender);
    domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true } as any);
  }

  /**
   * Adds selected class to filter box option
   */
  addFilterBoxSelectedClass(key: string, value: string): void {
    const actionId = this.filterHelper.simpleMd5(`${key}-${value}`);
    const domElement = document.getElementById(actionId);
    if (domElement?.parentElement) {
      domElement.parentElement.classList.add('boost-sd__filter-option-box-item--selected');
    }
  }

  /**
   * Removes selected class from filter box option
   */
  removeFilterBoxSelectedClass(key: string, value: string): void {
    const actionId = this.filterHelper.simpleMd5(`${key}-${value}`);
    const domElement = document.getElementById(actionId);
    if (domElement?.parentElement) {
      domElement.parentElement.classList.remove('boost-sd__filter-option-box-item--selected');
    }
  }

  // ============================================
  // List Filter Methods
  // ============================================

  /**
   * Unified renderOptionList with lazy loading and full render support
   */
  async renderOptionList(params: {
    option: any;
    dom: HTMLElement;
    limit?: number;
    lazyLoad?: boolean;
    viewportId?: string;
  }): Promise<void> {
    const { option, dom, limit, lazyLoad = false, viewportId } = params;

    if (!dom) return;
    if (option.values?.length === 0) return;

    // Handle lazy loading
    if (lazyLoad) {
      this.setupLazyLoadOptionList(dom, viewportId || '', option);
      return;
    }

    // Handle full render from viewport
    if (viewportId) {
      const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort || {};
      if (filterTreeViewPort[viewportId]?.isRendered) {
        return;
      }
      filterTreeViewPort[viewportId] = {
        ...filterTreeViewPort[viewportId],
        isRendered: true,
      };
      this.filterStore.state.value.filterTreeViewPort = filterTreeViewPort;
    }

    // Normal render
    await this.renderListContent(dom, option, limit);
  }

  /**
   * Main rendering logic for list options
   */
  protected async renderListContent(dom: HTMLElement, option: any, limit?: number): Promise<void> {
    const selectedKeys = this.getSelectedKeysFilter();

    // Add current collection to selected keys if applicable
    const collectionId = this.appService.TAEAppConfig.generalSettings?.collection_id;
    if (collectionId) {
      selectedKeys[`collection-${collectionId}`] = true;
    }

    const classNameUnselected = `boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button ${
      option.selectType === 'multiple' ? 'boost-sd__filter-option-item-button--with-checkbox' : ''
    }`;
    const classNameSelected = `${classNameUnselected} boost-sd__filter-option-item-button--selected`;

    const size = limit && option.values.length > limit ? limit : option.values.length;
    const isXssDisabled = (this.appService.TAEAppConfig as any).xss?.filter?.disabled;
    let values = isXssDisabled
      ? this.getLimitOptionValues(option, size)
      : this.xssFilterOption(option, size);

    const label = option.label;

    // Support custom sort values
    if (typeof (window as any).boostCustomSortOptionValues === 'function') {
      values = (window as any).boostCustomSortOptionValues(values, option);
    }

    const showDocCount = this.appService.filterSettings?.showFilterOptionCount || false;

    // Handle "All" collection option
    if (option.filterType === 'collection' && option.activeCollectionAll) {
      const getIdCollectionAll = this.filterHelper.getLocalStorage(COLLECTION_ALL_STORAGE_KEY) || 0;
      values = [
        { key: getIdCollectionAll, label: 'All', displayName: 'All', handle: 'all' },
        ...values,
      ];
    }

    const params = {
      label,
      values,
      option,
      dom,
      showDocCount,
      selectedKeys,
      classNameSelected,
      classNameUnselected,
    };

    await this.manageViewMoreState(params);
  }

  /**
   * Sets up lazy loading for list options
   */
  protected setupLazyLoadOptionList(dom: HTMLElement, id: string, option: any): void {
    // Do not lazy load for View More options
    if (this.isViewMoreFilterOption(option)) return;

    const domMouseMove = dom.closest(FILTER_OPTION_SELECTOR);
    if (!domMouseMove) return;

    const mousemoveRender = this.filterHelper.debounce(() => {
      this.renderOptionList({
        option,
        dom,
        lazyLoad: false,
        viewportId: id,
      });
    }, 200);

    domMouseMove.addEventListener('mousemove', mousemoveRender);
    domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true } as any);
  }

  /**
   * Adds selected class to filter list option
   */
  addFilterListSelectedClass(key: string, value: string): void {
    const actionId = this.filterHelper.simpleMd5(`${key}-${value}`);
    const parent = this.filterService.getFilterTreeParent();
    const domElement = parent?.querySelector(`#${actionId}`);
    if (domElement) {
      domElement.classList.add('boost-sd__filter-option-item-button--selected');
    }
  }

  /**
   * Removes selected class from filter list option
   */
  removeFilterListSelectedClass(key: string, value: string): void {
    const actionId = this.filterHelper.simpleMd5(`${key}-${value}`);
    const parent = this.filterService.getFilterTreeParent();
    const domElement = parent?.querySelector(`#${actionId}`);
    if (domElement) {
      domElement.classList.remove('boost-sd__filter-option-item-button--selected');
    }
  }

  /**
   * Adds selected class to filter option based on display type
   */
  addFilterSelectedClass(key: string, value: string, displayType: string = 'list'): void {
    if (displayType === 'box') {
      this.addFilterBoxSelectedClass(key, value);
      return;
    }
    if (displayType === 'swatch') {
      this.addFilterSwatchSelectedClass(key, value);
      return;
    }

    this.addFilterListSelectedClass(key, value);
  }

  /**
   * Removes selected class from filter option based on display type
   */
  removeFilterSelectedClass(key: string, value: string, displayType: string): void {
    if (displayType === 'box') {
      this.removeFilterBoxSelectedClass(key, value);
      return;
    }
    if (displayType === 'swatch') {
      this.removeFilterSwatchSelectedClass(key, value);
      return;
    }

    this.removeFilterListSelectedClass(key, value);
  }

  // ============================================
  // Swatch Filter Methods
  // ============================================

  /**
   * Unified renderOptionSwatch with lazy loading and full render support
   */
  async renderOptionSwatch(params: {
    option: any;
    dom: HTMLElement;
    limit?: number;
    lazyLoad?: boolean;
    viewportId?: string;
  }): Promise<void> {
    const { option, dom, limit, lazyLoad = false, viewportId } = params;

    if (!dom) return;

    // Handle full render with viewport tracking
    if (viewportId && this.filterStore.state.value.filterTreeViewPort) {
      const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort;
      if (filterTreeViewPort[viewportId]?.isRendered) {
        return;
      }
      filterTreeViewPort[viewportId] = {
        ...filterTreeViewPort[viewportId],
        isRendered: true,
      };
    }

    // Normal render
    await this.renderSwatchContent(dom, option, limit);

    // Setup lazy loading if requested
    if (lazyLoad && viewportId) {
      this.setupLazyLoadOptionSwatch(dom, viewportId, option);
    }
  }

  /**
   * Main rendering logic for swatch options
   */
  protected async renderSwatchContent(
    dom: HTMLElement,
    option: any,
    limit?: number
  ): Promise<void> {
    const selectedKeys = this.getSelectedKeysFilter();
    const [swatchShape, swatchType] = (option.swatchStyle || 'circle-grid')?.split('-') || [];

    const classNameUnselected = `boost-sd__filter-option-swatch-item-img boost-sd__filter-option-swatch-item-img--${swatchType} boost-sd__filter-option-swatch-item-img--${swatchShape}`;
    const classNameSelected = `${classNameUnselected} boost-sd__filter-option-swatch-item-img--selected`;

    const size = limit && option.values.length > limit ? limit : option.values.length;
    const isXssDisabled = (this.appService.TAEAppConfig as any)?.xss?.filter?.disabled || false;
    const values = isXssDisabled
      ? this.getLimitOptionValues(option, size)
      : this.xssFilterOption(option, size);
    const label = option.label;

    const { showFilterOptionCount = false } = this.appService.filterSettings;
    const showDocCount = showFilterOptionCount || false;

    const params = {
      option,
      values,
      dom,
      label,
      swatchShape,
      swatchType,
      showDocCount,
      selectedKeys,
      classNameSelected,
      classNameUnselected,
    };

    await this.manageViewMoreState(params);
  }

  /**
   * Sets up lazy loading for swatch options
   */
  protected setupLazyLoadOptionSwatch(dom: HTMLElement, id: string, option: any): void {
    // Do not lazy load for View More options
    const shouldPrevent = this.isViewMoreFilterOption(option);
    if (shouldPrevent) return;

    const domMouseMove = dom?.closest('.boost-sd__filter-option');

    if (!domMouseMove) return;

    const mousemoveRender = this.filterHelper.debounce(() => {
      this.renderOptionSwatch({
        option,
        dom,
        lazyLoad: false,
        viewportId: id,
      });
    }, 200);

    domMouseMove.addEventListener('mousemove', mousemoveRender);
    domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true } as any);
  }

  /**
   * Adds selected class to swatch filter option
   */
  addFilterSwatchSelectedClass(key: string, value: string): void {
    const actionId = this.filterHelper.simpleMd5(`${key}-${value}`);
    const domElement = document.getElementById(actionId);
    if (domElement?.parentElement) {
      domElement.parentElement.classList.add('boost-sd__filter-option-swatch-item-img--selected');
    }
  }

  /**
   * Removes selected class from swatch filter option
   */
  removeFilterSwatchSelectedClass(key: string, value: string): void {
    const actionId = this.filterHelper.simpleMd5(`${key}-${value}`);
    const domElement = document.getElementById(actionId);
    if (domElement?.parentElement) {
      domElement.parentElement.classList.remove(
        'boost-sd__filter-option-swatch-item-img--selected'
      );
    }
  }

  // ============================================
  // Rating Filter Methods
  // ============================================

  /**
   * Renders rating filter options with star displays.
   *
   * Generates HTML for review rating filters, showing star icons and product counts
   * for each rating level. Commonly used with review integration services.
   *
   * @param params - Rating rendering parameters
   * @param params.option - Filter option configuration
   * @param params.values - Array of rating values (e.g., 4-5 stars, 3-4 stars)
   * @param params.dom - Container element for the rating options
   * @param params.label - Filter option label (typically "Rating")
   *
   * @remarks
   * Rating values are typically ranges (from/to) representing star ratings.
   * The template renders filled/empty stars based on the rating value.
   *
   * @example
   * Override to customize rating display:
   * ```typescript
   * async renderOptionRating(params) {
   *   await super.renderOptionRating(params);
   *   // Add review count text
   *   params.dom.querySelectorAll('.boost-sd__rating-item').forEach(item => {
   *     const count = item.getAttribute('data-doc-count');
   *     item.insertAdjacentHTML('beforeend',
   *       `<span class="review-count">(${count} reviews)</span>`
   *     );
   *   });
   * }
   * ```
   */
  async renderOptionRating(params: {
    option: any;
    dom: HTMLElement;
    limit?: number;
    lazyLoad?: boolean;
    viewportId?: string;
  }): Promise<void> {
    const { option, dom, limit, lazyLoad = false, viewportId } = params;

    if (!dom) return;

    // Handle lazy loading
    if (lazyLoad) {
      this.setupLazyLoadOptionRating(dom, viewportId || '', option);
      return;
    }

    // Handle full render from viewport
    if (viewportId) {
      const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort || {};
      if (filterTreeViewPort[viewportId]?.isRendered) {
        return;
      }
      filterTreeViewPort[viewportId] = {
        ...filterTreeViewPort[viewportId],
        isRendered: true,
      };
      this.filterStore.state.value.filterTreeViewPort = filterTreeViewPort;
    }

    // Normal render
    await this.renderRatingContent(dom, option, limit);
  }

  /**
   * Main rendering logic for rating options
   */
  protected async renderRatingContent(
    dom: HTMLElement,
    option: any,
    limit?: number
  ): Promise<void> {
    const selectedKeys = this.getSelectedKeysFilter();

    const classNameUnselected = `boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button ${
      option.selectType === 'multiple' ? 'boost-sd__filter-option-item-button--with-checkbox' : ''
    }`;
    const classNameSelected = `${classNameUnselected} boost-sd__filter-option-item-button--selected`;

    const actionMapping = this.filterStore.getState('actionMapping') || {};

    let html = '';
    const size = limit && option.values.length > limit ? limit : option.values.length;
    const isXssDisabled = (this.appService.TAEAppConfig as any).xss?.filter?.disabled;
    const values = isXssDisabled
      ? this.getLimitOptionValues(option, size)
      : this.xssFilterOption(option, size);
    const template = this.filterHelper.getTemplate('filterOptionRatingTemplate');
    const { showFilterOptionCount } = this.appService.filterSettings;

    const showDocCount = showFilterOptionCount;
    for (const value of values) {
      const { key } = value;
      const actionId = this.filterHelper.simpleMd5(`${option.label}-${key}`);
      const isSelected = selectedKeys[`${option.label}-${key}`];
      const className = isSelected ? classNameSelected : classNameUnselected;
      const dataAction = `${option.filterOptionId}.${actionId}`;

      actionMapping[option.filterOptionId] = actionMapping[option.filterOptionId] || {};
      actionMapping[option.filterOptionId][actionId] = {
        optionList: {
          key: option.filterOptionId,
          value: value.key,
          label: option.label,
          filterType: option.filterType,
          selectType: option.selectType,
          displayType: option.displayType,
          valueDisplay: value.label || '',
          handle: value.handle || '',
          actionId,
        },
      };
      const rendered = await this.appService.templateRender(template, {
        starColor: option.starColor,
        showExactRating: option.showExactRating,
        actionId,
        dataAction,
        value,
        className,
        showDocCount,
      });

      html += rendered;
    }
    const currentActionMapping = this.filterStore.getState('actionMapping') || {};
    this.filterStore.setState({ actionMapping: { ...currentActionMapping, ...actionMapping } });
    dom.innerHTML = html;

    // Emit event after render
    this.filterHelper.emit(EVENT_NAMES.FILTER_OPTION_RENDERED as any);
  }

  /**
   * Sets up lazy loading for rating options
   */
  protected setupLazyLoadOptionRating(dom: HTMLElement, id: string, option: any): void {
    const domMouseMove = dom.closest(FILTER_OPTION_SELECTOR);
    if (!domMouseMove) return;

    const mousemoveRender = this.filterHelper.debounce(() => {
      this.renderOptionRating({
        option,
        dom,
        lazyLoad: false,
        viewportId: id,
      });
    }, 200);

    domMouseMove.addEventListener('mousemove', mousemoveRender);
    domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true } as any);
  }

  // ============================================
  // Multi-Level Collection Methods
  // ============================================

  /**
   * Renders multi-level collection filter with hierarchical structure.
   *
   * Generates HTML for nested collection filters that support parent-child
   * relationships. Includes expand/collapse controls for subcollections.
   *
   * @param params - Multi-level collection rendering parameters
   * @param params.option - Filter option configuration
   * @param params.values - Array of collection values with potential children
   * @param params.dom - Container element for the collection tree
   * @param params.label - Filter option label
   *
   * @remarks
   * This method recursively renders subcollections and manages their
   * expand/collapse state. Override to customize collection hierarchy display.
   *
   * @example
   * Override to add collection icons:
   * ```typescript
   * async renderOptionMultiLevelCollection(params) {
   *   await super.renderOptionMultiLevelCollection(params);
   *   // Add collection icons
   *   params.dom.querySelectorAll('.collection-item').forEach(item => {
   *     const handle = item.getAttribute('data-handle');
   *     const icon = this.getCollectionIcon(handle);
   *     item.insertAdjacentHTML('afterbegin', `<img src="${icon}" class="col-icon"/>`);
   *   });
   * }
   * ```
   */
  async renderOptionMultiLevelCollection(params: {
    option: any;
    dom: HTMLElement;
    limit?: number;
    lazyLoad?: boolean;
    viewportId?: string;
  }): Promise<void> {
    const { option, dom, limit, lazyLoad = false, viewportId } = params;

    if (!dom) return;

    // Handle lazy loading
    if (lazyLoad) {
      this.setupLazyLoadMultiLevelCollection(dom, viewportId || '', option);
      return;
    }

    // Handle full render from viewport
    if (viewportId) {
      const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort || {};
      if (filterTreeViewPort[viewportId]?.isRendered) {
        return;
      }
      filterTreeViewPort[viewportId] = {
        ...filterTreeViewPort[viewportId],
        isRendered: true,
      };
      this.filterStore.state.value.filterTreeViewPort = filterTreeViewPort;
    }

    // Normal render
    await this.renderMultiLevelCollectionContent(dom, option, limit);
  }

  /**
   * Main rendering logic for multi-level collection
   */
  protected async renderMultiLevelCollectionContent(
    dom: HTMLElement,
    option: any,
    limit?: number
  ): Promise<void> {
    // Get selected keys
    const selectedKeys = this.getSelectedKeysFilter();

    // Add current collection to selected keys
    const collectionId = this.appService.TAEAppConfig.generalSettings?.collection_id;
    if (collectionId) {
      selectedKeys[`collection-${collectionId}`] = true;
    }

    // Add current collection tags to selected keys
    const collectionTags = this.appService.TAEAppConfig.generalSettings?.collection_tags;
    if (collectionTags && collectionTags.length > 0) {
      collectionTags.forEach((tag: string) => (selectedKeys[`tag-${tag}`] = true));
    }

    const classNameUnselected = `boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-link`;
    const classNameSelected =
      classNameUnselected + ' boost-sd__filter-option-item-button--selected';

    let html = '';
    const size = limit && option.values.length > limit ? limit : option.values.length;
    const isXssDisabled = (this.appService.TAEAppConfig as any).xss?.filter?.disabled;
    const values = isXssDisabled
      ? this.getLimitOptionValues(option, size)
      : this.xssFilterOption(option, size);
    const showDocCount = this.appService.filterSettings?.showFilterOptionCount;

    // Handle "All" collection option
    if (option.filterType === 'collection' && option.activeCollectionAll) {
      const getIdCollectionAll = this.filterHelper.getLocalStorage(COLLECTION_ALL_STORAGE_KEY) || 0;
      const valueCollectionAll = (window as any).boostSdCustomCollectionALl || {
        key: getIdCollectionAll,
        label: 'All',
        displayName: 'All',
        handle: 'all',
      };

      values?.unshift(valueCollectionAll);
    }

    // Build HTML for each value
    for (const value of values) {
      const actionId = this.filterHelper.simpleMd5(`${option.filterOptionId}-${value.key}`);
      const isDisabled = option.keepValuesStatic && value.doc_count === 0;
      let className = classNameUnselected;
      value.isDisabled = isDisabled;

      if (selectedKeys[`${option.filterOptionId}-${value.key}`]) {
        className = classNameSelected;
      }

      const metaData = {
        action: {
          optionCollection: {
            key: option.filterOptionId,
            value: value.key,
            label: option.label,
            filterType: option.filterType,
            displayName: value.displayName || value.label,
            handle: value.handle,
            actionId,
          },
        },
      };

      if (value.tags?.length > 0) {
        value.existedTags = true;
        const actionIdArrow = this.filterHelper.simpleMd5(
          `${option.filterOptionId}-arrow-${value.key}`
        );
        const targetIdList = this.filterHelper.simpleMd5(
          `${option.filterOptionId}-target-${value.key}`
        );
        const targetIdArrow = this.filterHelper.simpleMd5(
          `${option.filterOptionId}-target-arrow-${value.key}`
        );

        value.actionIdArrow = actionIdArrow;
        value.targetIdList = targetIdList;
        value.targetIdArrow = targetIdArrow;

        value.metaDataArrow = {
          action: {
            expandCollapseMultiLevel: {
              actionId: actionIdArrow,
              targets: [
                {
                  targetId: targetIdList,
                  classToggle: 'boost-sd__g-hide',
                  shouldAddClass: true,
                },
                {
                  targetId: targetIdArrow,
                  classToggle: 'boost-sd__arrow-icon--down',
                  shouldAddClass: false,
                },
              ],
            },
          },
        };

        value.tags.forEach((_tag: any) => {
          // assign actionId and metadata level2
          _tag.actionId = this.filterHelper.simpleMd5(`${FILTER_TAG_KEY}-${_tag.tag}`);
          _tag.targetId = this.filterHelper.simpleMd5(`${FILTER_TAG_KEY}-target-${_tag.tag}`);

          _tag.metaData = {
            action: {
              optionCollection: {
                key: option.filterOptionId,
                value: value.key,
                displayName: value.displayName,
                collectionTag: this.filterHelper.stripHtml(_tag.tag),
                label: option.label,
                handle: value.handle,
                tagDisplayName: _tag.displayName,
                filterType: option.filterType,
                valueDisplay: _tag.label || '',
                actionId: _tag.actionId,
              },
            },
          };

          _tag.className = classNameUnselected;
          if (selectedKeys[`${FILTER_TAG_KEY}-${_tag?.tag?.toLowerCase()}`]) {
            _tag.className = classNameSelected;
          }

          if (_tag.subTags?.length > 0) {
            _tag.existedSubTags = true;
            const actionIdArrowLevel2 = this.filterHelper.simpleMd5(
              `${option.filterOptionId}-arrow-level2-${_tag.tag}`
            );
            const targetIdListLevel2 = this.filterHelper.simpleMd5(
              `${option.filterOptionId}-target-level2-${_tag.tag}`
            );
            const targetIdArrowLevel2 = this.filterHelper.simpleMd5(
              `${option.filterOptionId}-target-arrow-level2-${_tag.tag}`
            );

            _tag.actionIdArrow = actionIdArrowLevel2;
            _tag.targetIdList = targetIdListLevel2;
            _tag.targetIdArrow = targetIdArrowLevel2;

            _tag.metaDataArrow = {
              action: {
                expandCollapseMultiLevel: {
                  actionId: actionIdArrowLevel2,
                  targets: [
                    {
                      targetId: targetIdListLevel2,
                      classToggle: 'boost-sd__g-hide',
                      shouldAddClass: true,
                    },
                    {
                      targetId: targetIdArrowLevel2,
                      classToggle: 'boost-sd__arrow-icon--down',
                      shouldAddClass: false,
                    },
                  ],
                },
              },
            };

            _tag.subTags.forEach((_subTag: any) => {
              // assign actionId and metadata level3
              _subTag.actionId = this.filterHelper.simpleMd5(`${FILTER_TAG_KEY}-${_subTag.tag}`);
              _subTag.targetId = this.filterHelper.simpleMd5(
                `${FILTER_TAG_KEY}-target-${_subTag.tag}`
              );
              _subTag.metaData = {
                action: {
                  optionCollection: {
                    key: option.filterOptionId,
                    value: value.key,
                    displayName: value.displayName,
                    collectionTag: this.filterHelper.stripHtml(_subTag.tag),
                    label: option.label,
                    handle: value.handle,
                    tagDisplayName: _subTag.displayName,
                    filterType: option.filterType,
                    valueDisplay: _subTag.label || '',
                    actionId: _subTag.actionId,
                  },
                },
              };

              _subTag.className = classNameUnselected;
              if (selectedKeys[`${FILTER_TAG_KEY}-${_subTag?.tag?.toLowerCase()}`]) {
                _subTag.className = classNameSelected;
              }
            });
          }
        });
      }

      const locale = ((window as any)?.Shopify?.routes?.root || '/').replace(/\//g, '');

      const filterOptionMultiLevelCollectionTemplate = this.filterHelper.getTemplate(
        'filterOptionMultiLevelCollection'
      );
      html += await this.appService.templateRender(filterOptionMultiLevelCollectionTemplate, {
        option,
        actionId,
        metaData,
        value,
        className,
        locale,
        showDocCount: value.handle === 'all' ? false : showDocCount,
      });
    }

    dom.innerHTML = html;

    // Keep state for multi-level items
    this.keepStateMultipleLevelOptionItems();

    // Dispatch custom event
    this.filterHelper.emit(EVENT_NAMES.FILTER_OPTION_RENDERED);
  }

  /**
   * Sets up lazy loading for multi-level collection
   */
  protected setupLazyLoadMultiLevelCollection(dom: HTMLElement, id: string, option: any): void {
    const domMouseMove = dom.closest('.boost-sd__filter-option');
    if (!domMouseMove) return;

    const mousemoveRender = this.filterHelper.debounce(() => {
      this.renderOptionMultiLevelCollection({
        option,
        dom,
        lazyLoad: false,
        viewportId: id,
      });
    }, 200);

    domMouseMove.addEventListener('mousemove', mousemoveRender);
    domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true } as any);
  }

  /**
   * Keeps state for multiple level option items
   */
  protected keepStateMultipleLevelOptionItems(): void {
    const { keepToggleState } = this.appService.filterSettings;
    if (!keepToggleState) return;

    const collapseStateMultipleLevel =
      this.filterStore.getState('collapseStateMultipleLevel') || {};
    const keysMultipleLevel = Object.keys(collapseStateMultipleLevel);

    keysMultipleLevel.forEach((key) => {
      this.handleCollapse(collapseStateMultipleLevel[key]);
    });
  }

  // ============================================
  // Multi-Level Tag Methods
  // ============================================

  /**
   * Renders multi-level tag filter with hierarchical structure.
   *
   * Generates HTML for nested tag filters supporting parent-child relationships.
   * Similar to multi-level collections but for product tags.
   *
   * @param params - Multi-level tag rendering parameters
   * @param params.option - Filter option configuration
   * @param params.values - Array of tag values with potential subtags
   * @param params.dom - Container element for the tag tree
   * @param params.label - Filter option label
   *
   * @remarks
   * Tags can be nested to arbitrary depth. The template system handles
   * indentation and expand/collapse controls automatically.
   *
   * @example
   * Override to add tag badges:
   * ```typescript
   * async renderOptionMultiLevelTag(params) {
   *   await super.renderOptionMultiLevelTag(params);
   *   // Add "New" badge to recently created tags
   *   params.dom.querySelectorAll('.tag-item').forEach(item => {
   *     const tag = item.getAttribute('data-tag');
   *     if (this.isNewTag(tag)) {
   *       item.insertAdjacentHTML('beforeend', '<span class="badge-new">New</span>');
   *     }
   *   });
   * }
   * ```
   */
  async renderOptionMultiLevelTag(params: {
    option: any;
    dom: HTMLElement;
    limit?: number;
    lazyLoad?: boolean;
    viewportId?: string;
    firstRender?: boolean;
  }): Promise<void> {
    const { option, dom, limit, lazyLoad = false, viewportId, firstRender = false } = params;

    if (!dom) return;

    // Handle lazy loading
    if (lazyLoad) {
      this.setupLazyLoadMultiLevelTag(dom, viewportId || '', option);
      return;
    }

    // Handle full render from viewport
    if (viewportId) {
      const filterTreeViewPort = this.filterStore.state.value.filterTreeViewPort || {};
      if (filterTreeViewPort[viewportId]?.isRendered) {
        return;
      }
      filterTreeViewPort[viewportId] = {
        ...filterTreeViewPort[viewportId],
        isRendered: true,
      };
      this.filterStore.state.value.filterTreeViewPort = filterTreeViewPort;
    }

    // Normal render
    await this.renderMultiLevelTagContent(dom, option, limit, firstRender);
  }

  /**
   * Main rendering logic for multi-level tag
   */
  protected async renderMultiLevelTagContent(
    dom: HTMLElement,
    option: any,
    limit?: number,
    firstRender: boolean = false
  ): Promise<void> {
    // Get selected keys
    const selectedKeys = this.getSelectedKeysFilter();

    const classNameUnselected = `boost-sd__filter-option-item-button boost-sd__filter-option-item-button--as-button ${
      option.selectType === 'multiple' ? 'boost-sd__filter-option-item-button--with-checkbox' : ''
    }`;
    const classNameSelected =
      classNameUnselected + ' boost-sd__filter-option-item-button--selected';

    const showFilterOptionCount = this.appService.filterSettings?.showFilterOptionCount;
    const showProductCount = showFilterOptionCount;

    let html = '';
    const size = limit && option.values.length > limit ? limit : option.values.length;
    const isXssDisabled = (this.appService.TAEAppConfig as any).xss?.filter?.disabled;
    const values = isXssDisabled
      ? this.getLimitOptionValues(option, size)
      : this.xssFilterOption(option, size);
    const label = option.label;

    // Build HTML for each value
    for (const value of values) {
      const actionId = this.filterHelper.simpleMd5(`${option.filterOptionId}-${value.tag}`);
      let className = classNameUnselected;
      if (selectedKeys[`${option.filterOptionId}-${value.tag?.toLowerCase()}`]) {
        className = classNameSelected;
      }

      const metaData = {
        action: {
          optionList: {
            key: option.filterOptionId,
            value: value.tag,
            label: option.label,
            filterType: option.filterType,
            selectType: option.selectType,
            valueDisplay: value.displayName || '',
          },
        },
      };

      if (value.subTags?.length > 0 && !firstRender) {
        const actionIdArrow = this.filterHelper.simpleMd5(
          `${option.filterOptionId}-arrow-${value.tag}`
        );
        const targetIdList = this.filterHelper.simpleMd5(
          `${option.filterOptionId}-target-${value.tag}`
        );
        const targetIdArrow = this.filterHelper.simpleMd5(
          `${option.filterOptionId}-target-arrow-${value.tag}`
        );

        value.actionIdArrow = actionIdArrow;
        value.targetIdList = targetIdList;
        value.targetIdArrow = targetIdArrow;

        value.metaDataArrow = {
          action: {
            expandCollapseMultiLevel: {
              actionId: actionIdArrow,
              targets: [
                {
                  targetId: targetIdList,
                  classToggle: 'boost-sd__g-hide',
                  shouldAddClass: true,
                },
                {
                  targetId: targetIdArrow,
                  classToggle: 'boost-sd__arrow-icon--down',
                  shouldAddClass: false,
                },
              ],
            },
          },
        };

        value.subTags.forEach((_subTag: any) => {
          // assign actionId and metadata level2
          _subTag.actionId = this.filterHelper.simpleMd5(`${option.filterOptionId}-${_subTag.tag}`);
          _subTag.targetId = this.filterHelper.simpleMd5(
            `${option.filterOptionId}-target-${_subTag.tag}`
          );

          _subTag.metaData = {
            action: {
              optionList: {
                key: option.filterOptionId,
                value: _subTag.tag,
                label: option.label,
                filterType: option.filterType,
                selectType: option.selectType,
                valueDisplay: _subTag.displayName || '',
              },
            },
          };

          _subTag.className = classNameUnselected;
          if (selectedKeys[`${option.filterOptionId}-${_subTag.tag?.toLowerCase()}`]) {
            _subTag.className = classNameSelected;
          }

          if (_subTag.subTags?.length > 0) {
            const actionIdArrow = this.filterHelper.simpleMd5(
              `${option.filterOptionId}-arrow-level2-${_subTag.tag}`
            );
            const targetIdList = this.filterHelper.simpleMd5(
              `${option.filterOptionId}-target-level2-${_subTag.tag}`
            );
            const targetIdArrow = this.filterHelper.simpleMd5(
              `${option.filterOptionId}-target-arrow-level2-${_subTag.tag}`
            );

            _subTag.actionIdArrow = actionIdArrow;
            _subTag.targetIdList = targetIdList;
            _subTag.targetIdArrow = targetIdArrow;

            _subTag.metaDataArrow = {
              action: {
                expandCollapseMultiLevel: {
                  actionId: actionIdArrow,
                  targets: [
                    {
                      targetId: targetIdList,
                      classToggle: 'boost-sd__g-hide',
                      shouldAddClass: true,
                    },
                    {
                      targetId: targetIdArrow,
                      classToggle: 'boost-sd__arrow-icon--down',
                      shouldAddClass: false,
                    },
                  ],
                },
              },
            };

            _subTag.subTags.forEach((_subTag2: any) => {
              // assign actionId and metadata level3
              _subTag2.actionId = this.filterHelper.simpleMd5(
                `${option.filterOptionId}-${_subTag2.tag}`
              );
              _subTag2.targetId = this.filterHelper.simpleMd5(
                `${option.filterOptionId}-target-${_subTag2.tag}`
              );
              _subTag2.metaData = {
                action: {
                  optionList: {
                    key: option.filterOptionId,
                    value: _subTag2.tag,
                    label: option.label,
                    filterType: option.filterType,
                    selectType: option.selectType,
                    valueDisplay: _subTag2.displayName || '',
                  },
                },
              };

              _subTag2.className = classNameUnselected;
              if (selectedKeys[`${option.filterOptionId}-${_subTag2.tag?.toLowerCase()}`]) {
                _subTag2.className = classNameSelected;
              }
            });
          }
        });
      }

      const filterOptionMultiLevelTagTemplate = this.filterHelper.getTemplate(
        'filterOptionMultiLevelTagTemplate'
      );
      html += await this.appService.templateRender(filterOptionMultiLevelTagTemplate, {
        option,
        actionId,
        metaData,
        value,
        label,
        className,
        showProductCount,
      });
    }

    dom.innerHTML = html;

    // Keep state for multi-level items
    this.keepStateMultipleLevelOptionItems();

    // Dispatch custom event
    this.filterHelper.emit(EVENT_NAMES.FILTER_OPTION_RENDERED);

    // Always register lazy load with multiple level tags on first render
    if (firstRender) {
      this.setupLazyLoadMultiLevelTag(dom, option.filterOptionId, option);
    }
  }

  /**
   * Sets up lazy loading for multi-level tag
   */
  protected setupLazyLoadMultiLevelTag(dom: HTMLElement, id: string, option: any): void {
    const domMouseMove = dom.closest('.boost-sd__filter-option');
    if (!domMouseMove) return;

    const mousemoveRender = this.filterHelper.debounce(() => {
      this.renderOptionMultiLevelTag({
        option,
        dom,
        lazyLoad: false,
        viewportId: id,
      });
    }, 200);

    domMouseMove.addEventListener('mousemove', mousemoveRender);
    domMouseMove.addEventListener('touchstart', mousemoveRender, { passive: true } as any);
  }

  // ============================================
  // Range Slider Support Methods
  // ============================================

  /**
   * Generates filter data object for range filters
   */
  generateFilterDataForRange(key: string, value: any, option: any): any {
    return {
      data: {
        key,
        value: this.filterHelper.toRangeValue(value),
        label: option.label,
        moneyFormatValue: option?.moneyFormatValue || '',
      },
      metaData: {
        key,
        value: this.filterHelper.toStringValue(value),
        type: option.displayType || 'range',
      },
      type: option.displayType || 'range',
    };
  }

  /**
   * Dispatches a `boost-sd-range-slider-change` custom event for analytics.
   *
   * @param data - Event payload with the filter label and selected min/max values
   */
  protected dispatchEventChangeRangeSlider(data: { label: string; min: any; max: any }): void {
    const event = new CustomEvent('boost-sd-range-slider-change', { detail: data });
    window.dispatchEvent(event);
  }

  /**
   * Renders range slider for price/rating filters.
   *
   * Orchestrates DOM initialization, keyboard/blur/drag interaction wiring,
   * and listener cleanup for the custom range slider widget.
   */
  renderOptionRangeSlider({ dom, option }: { dom: HTMLElement; option: any }): void {
    if (option.values.min === option.values.max) return;

    const ctx = this.buildSliderContext(dom, option);
    if (!ctx) return;

    this.initHandlePositions(ctx);

    this.attachKeyboardInteraction(ctx.minHandle, 'lower', ctx);
    this.attachKeyboardInteraction(ctx.maxHandle, 'upper', ctx);

    if (ctx.inputLower) {
      this.attachInputBlurHandler(ctx.inputLower, 'lower', ctx);
      ctx.inputLower.value = String(
        this.normalizeRangeValue(ctx.sliderOption.action.value.lower ?? ctx.min, ctx.removeDecimal)
      );
    }

    if (ctx.inputUpper) {
      this.attachInputBlurHandler(ctx.inputUpper, 'upper', ctx);
      ctx.inputUpper.value = String(
        this.normalizeRangeValue(ctx.sliderOption.action.value.upper ?? ctx.max, ctx.removeDecimal)
      );
    }

    const { onDown, onMove, onUp } = this.createDragHandlers(ctx);

    // Remove any previously registered listeners before re-attaching (prevents accumulation).
    // Keyed by filterOptionId rather than the slider Element so cleanup runs even when
    // the DOM is fully replaced by lazyLoadFilterTree (oldDOM.replaceWith(...)).
    const cleanupKey = ctx.option.filterOptionId;
    this.sliderCleanupMap.get(cleanupKey)?.();

    ctx.slider.addEventListener('mousedown', onDown);
    ctx.slider.addEventListener('touchstart', onDown, { passive: true } as any);
    ctx.slider.addEventListener('mousemove', onMove);
    ctx.slider.addEventListener('touchmove', onMove, { passive: true } as any);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchend', onUp, { passive: true } as any);

    this.sliderCleanupMap.set(cleanupKey, () => {
      ctx.slider.removeEventListener('mousedown', onDown);
      ctx.slider.removeEventListener('touchstart', onDown);
      ctx.slider.removeEventListener('mousemove', onMove);
      ctx.slider.removeEventListener('touchmove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchend', onUp);
    });

    this.formatRangeSliderCurrency(dom);
  }

  // ============================================
  // Range Slider — Context & Position Helpers
  // ============================================

  /**
   * Collects all DOM references and computed settings into a single context object.
   * Returns null if the slider DOM node is not present in the container.
   */
  protected buildSliderContext(dom: HTMLElement, option: any): RangeSliderContext | null {
    const slider = dom.querySelector('.boost-sd__filter-option-range-slider-slider');
    if (!slider) return null;

    const { min, max } = option.values;
    const filterState = this.filterStore.getState('selectedFilter') || [];
    const rangeFilter = this.filterStore.getState('rangeFilter') || {};
    const sliderStep = this.sanitizeSliderStep(option.sliderStep, option.sliderRange, min, max);

    const { filterLayout, requestInstantly } = this.appService.filterSettings;
    const isFilterVertical = filterLayout !== 'horizontal' || this.filterHelper.isMobile();
    const horizontalHasApplyButton =
      filterLayout === 'horizontal' && !requestInstantly && !this.filterHelper.isMobile();

    const sliderBase = slider.querySelector('.noUi-base') as HTMLElement;
    const minHandle = slider.querySelector('.noUi-handle-lower') as HTMLElement;
    const maxHandle = slider.querySelector('.noUi-handle-upper') as HTMLElement;

    const inputLower = dom.querySelector(
      '.boost-sd__filter-option-range-slider-input[aria-label="Min value"]'
    ) as HTMLInputElement;
    const inputUpper = dom.querySelector(
      '.boost-sd__filter-option-range-slider-input[aria-label="Max value"]'
    ) as HTMLInputElement;

    const sliderOption = this.initSliderOption(option, filterState, inputLower, inputUpper);

    // Restore last active handle from previously persisted state.
    const persistedSliderOption = rangeFilter[option.filterOptionId];
    if (persistedSliderOption?.lastActiveHandle) {
      sliderOption.lastActiveHandle = persistedSliderOption.lastActiveHandle;
    }

    const removeDecimal = !!(window as any).boostWidgetIntegrationConfig?.filter?.rangeSlider
      ?.removeDecimal;

    return {
      min,
      max,
      valueRange: max - min,
      sliderStep,
      option,
      sliderOption,
      rangeFilter,
      filterState,
      isFilterVertical,
      horizontalHasApplyButton,
      removeDecimal,
      slider,
      sliderBase,
      minHandle,
      maxHandle,
      inputLower,
      inputUpper,
    };
  }

  /** Sets initial handle transforms and z-indexes from persisted slider state. */
  protected initHandlePositions(ctx: RangeSliderContext): void {
    const { minHandle, maxHandle, sliderOption } = ctx;
    this.applyHandleTranslate(minHandle, Math.abs(sliderOption.action.display.lower));
    this.applyHandleTranslate(maxHandle, sliderOption.action.display.upper);
    this.setActiveHandleZIndex(sliderOption.lastActiveHandle, minHandle, maxHandle);
  }

  // ============================================
  // Range Slider — Pure Value Helpers
  // ============================================

  /**
   * Normalizes a numeric slider value to the configured decimal precision.
   * Protected to allow override in shop-specific integrations.
   */
  protected normalizeRangeValue(val: number, removeDecimal: boolean): number {
    if (val === null || val === undefined || isNaN(val)) return val;
    const fixed = parseFloat(String(val)).toFixed(removeDecimal ? 0 : 2);
    return Number(this.filterHelper.replaceDotCommaZero(fixed));
  }

  /** Returns the CSS translate-X percent that positions a handle at the given value. */
  protected computeHandleTranslate(value: number, min: number, valueRange: number): number {
    return 100 - ((value - min) / valueRange) * 100;
  }

  /** Sanitizes sliderStep/sliderRange inputs, applying NaN fallbacks and the max-step cap. */
  protected sanitizeSliderStep(
    sliderStep: number,
    sliderRange: number,
    min: number,
    max: number
  ): number {
    const step = isNaN(sliderStep) ? 1 : sliderStep;
    const range = isNaN(sliderRange) ? 1 : sliderRange;
    return Math.min(step, (max - min) / range);
  }

  // ============================================
  // Range Slider — DOM Sync Helpers
  // ============================================

  /** Applies a CSS translate transform to a handle's parent origin element. */
  protected applyHandleTranslate(handle: HTMLElement, translatePercent: number): void {
    handle.parentElement!.style.transform = `translate(-${translatePercent}%, 0px)`;
  }

  /** Elevates the active handle to z-index 5 and lowers the inactive one to 4. */
  protected setActiveHandleZIndex(
    activeKey: 'lower' | 'upper',
    minHandle: HTMLElement,
    maxHandle: HTMLElement
  ): void {
    if (activeKey === 'lower') {
      minHandle.parentElement!.style.zIndex = '5';
      maxHandle.parentElement!.style.zIndex = '4';
    } else {
      maxHandle.parentElement!.style.zIndex = '5';
      minHandle.parentElement!.style.zIndex = '4';
    }
  }

  /** Writes a value to a text input; no-ops safely when the input is null. */
  protected syncInputValue(input: HTMLInputElement | null, value: number): void {
    if (input) input.value = String(value);
  }

  // ============================================
  // Range Slider — State Transition Helpers
  // ============================================

  /** Persists the current sliderOption into the rangeFilter store entry. */
  protected persistRangeState(ctx: RangeSliderContext): void {
    const { rangeFilter, option, sliderOption } = ctx;
    rangeFilter[option.filterOptionId] = sliderOption;
    this.filterStore.setState({ rangeFilter });
  }

  /**
   * Commits a resolved filter tree to the store, emits FILTER_RESET for vertical layouts,
   * and dispatches the range-slider-change analytics event.
   */
  protected commitFilterTreeChange(newFilterTree: any[], ctx: RangeSliderContext): void {
    const { isFilterVertical, option, inputLower, inputUpper, sliderOption } = ctx;
    this.filterStore.setState({ selectedFilter: newFilterTree });
    if (isFilterVertical) {
      this.filterHelper.emit(EVENT_NAMES.FILTER_RESET);
    }
    this.dispatchEventChangeRangeSlider({
      label: option.label,
      min: inputLower?.value ?? String(sliderOption.action.value.lower),
      max: inputUpper?.value ?? String(sliderOption.action.value.upper),
    });
  }

  /**
   * Applies a validated slider value: updates state, syncs DOM, persists to store,
   * and commits the filter tree change. Called by keyboard and blur interactions.
   * Protected to allow override in shop-specific integrations.
   */
  protected applySliderValue(key: 'lower' | 'upper', value: number, ctx: RangeSliderContext): void {
    const {
      min,
      valueRange,
      sliderOption,
      option,
      filterState,
      horizontalHasApplyButton,
      isFilterVertical,
      minHandle,
      maxHandle,
    } = ctx;
    const handle = key === 'lower' ? minHandle : maxHandle;
    const input = key === 'lower' ? ctx.inputLower : ctx.inputUpper;

    sliderOption.lastActiveHandle = key;
    value = this.normalizeRangeValue(value, ctx.removeDecimal);
    sliderOption.action.value[key] = value;

    const translatePercent = this.computeHandleTranslate(value, min, valueRange);
    this.syncInputValue(input, value);
    this.applyHandleTranslate(handle, translatePercent);
    this.setActiveHandleZIndex(key, minHandle, maxHandle);

    if (isFilterVertical) this.domHelper.scrollToTop({ isFilter: true });

    sliderOption.action.display[key] = translatePercent;
    sliderOption.status = 'selected';
    this.persistRangeState(ctx);

    const newFilterTree = this.addRangeValueToFilterTree(
      filterState,
      option,
      { key: option.filterOptionId, value: sliderOption.action.value },
      horizontalHasApplyButton
    );
    if (newFilterTree) {
      this.commitFilterTreeChange(newFilterTree, ctx);
    }
  }

  // ============================================
  // Range Slider — Interaction Handlers
  // ============================================

  /**
   * Attaches keyboard arrow-key navigation to a slider handle.
   * Moves the handle by sliderStep on ArrowLeft/ArrowRight, debounced to 500 ms.
   */
  protected attachKeyboardInteraction(
    handle: HTMLElement,
    type: 'lower' | 'upper',
    ctx: RangeSliderContext
  ): void {
    if (!handle) return;
    if (!handle.getAttribute('tabindex')) handle.setAttribute('tabindex', '0');

    let keyboardFilterTimer: any;

    handle.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      e.preventDefault();

      const { min, max, valueRange, sliderStep, sliderOption } = ctx;
      const delta = e.key === 'ArrowRight' ? sliderStep : -sliderStep;
      const current = sliderOption.action.value[type];
      const opposite = sliderOption.action.value[type === 'lower' ? 'upper' : 'lower'];

      let nextValue = current + delta;
      nextValue =
        type === 'lower'
          ? Math.max(min, Math.min(nextValue, opposite))
          : Math.min(max, Math.max(nextValue, opposite));

      if (nextValue === current) return;

      nextValue = this.normalizeRangeValue(nextValue, ctx.removeDecimal);
      const translatePercent = this.computeHandleTranslate(nextValue, min, valueRange);
      this.applyHandleTranslate(handle, translatePercent);

      clearTimeout(keyboardFilterTimer);
      keyboardFilterTimer = setTimeout(() => {
        this.applySliderValue(type, nextValue, ctx);
      }, 500);
    });
  }

  /**
   * Attaches a blur handler that validates the typed value and calls applySliderValue
   * when the committed value differs from the current slider state.
   * Uses blur (not change) so it only fires on actual user interaction.
   */
  protected attachInputBlurHandler(
    input: HTMLInputElement,
    type: 'lower' | 'upper',
    ctx: RangeSliderContext
  ): void {
    input.addEventListener('blur', (event: Event) => {
      const target = event.target as HTMLInputElement;
      let value = Number(target.value);
      const { min, max, sliderOption } = ctx;
      const currentValue = sliderOption.action.value[type];
      const opposite = sliderOption.action.value[type === 'lower' ? 'upper' : 'lower'];

      const isInvalid =
        type === 'lower'
          ? value > opposite || value < min || value > max
          : value < opposite || value < min || value > max;

      if (isInvalid) {
        const fallback =
          type === 'lower'
            ? sliderOption.action.value.lower || min
            : sliderOption.action.value.upper || max;
        value = fallback;
        input.value = String(this.normalizeRangeValue(value, ctx.removeDecimal));
      }

      if (value !== currentValue) {
        this.applySliderValue(type, value, ctx);
      }
    });
  }

  /**
   * Creates the three drag event handlers (pointerdown, pointermove, pointerup equivalents).
   * Drag state is shared between the handlers through closure variables.
   */
  protected createDragHandlers(ctx: RangeSliderContext): {
    onDown: (e: Event) => void;
    onMove: (e: Event) => void;
    onUp: () => void;
  } {
    const {
      min,
      max,
      valueRange,
      sliderBase,
      minHandle,
      maxHandle,
      inputLower,
      inputUpper,
      sliderOption,
      rangeFilter,
      option,
      filterState,
      horizontalHasApplyButton,
      isFilterVertical,
    } = ctx;

    let isDragging = false;
    let activeHandle: HTMLElement | null = null;
    let activeHandleType: 'minHandle' | 'maxHandle' | null = null;

    const onDown = (event: Event) => {
      const handle = (event.target as HTMLElement).closest('.noUi-handle') as HTMLElement;
      if (!handle) return;

      isDragging = true;
      activeHandle = handle;

      if (handle.classList.contains('noUi-handle-lower')) {
        activeHandleType = 'minHandle';
        sliderOption.lastActiveHandle = 'lower';
        this.setActiveHandleZIndex('lower', minHandle, maxHandle);
      } else {
        activeHandleType = 'maxHandle';
        sliderOption.lastActiveHandle = 'upper';
        this.setActiveHandleZIndex('upper', minHandle, maxHandle);
      }
    };

    const onMove = (event: Event) => {
      if (!isDragging) return;

      const clientX =
        (event as MouseEvent).clientX ||
        (event as any).pageX ||
        (event as TouchEvent).touches?.[0]?.clientX;
      if (!clientX) return;

      const startX = sliderBase.getBoundingClientRect().left;
      const endX = startX + sliderBase.offsetWidth;
      if (clientX < startX - 2 || clientX > endX + 2) return;

      const percent = (clientX - startX) / sliderBase.offsetWidth;
      let newValue = this.normalizeRangeValue(percent * valueRange + min, ctx.removeDecimal);
      newValue = Math.min(Math.max(newValue, min), max);
      const translatePercent = 100 - percent * 100;

      if (activeHandleType === 'minHandle') {
        newValue = Math.min(newValue, sliderOption.action.value.upper);
        sliderOption.action.value.lower = newValue;
        this.syncInputValue(inputLower, newValue);
        sliderOption.action.display.lower = translatePercent;
      } else if (activeHandleType === 'maxHandle') {
        newValue = Math.max(newValue, sliderOption.action.value.lower);
        sliderOption.action.value.upper = newValue;
        this.syncInputValue(inputUpper, newValue);
        sliderOption.action.display.upper = translatePercent;
      }

      sliderOption.status = 'selected';
      activeHandle!.parentElement!.style.transform = `translate(-${translatePercent}%, 0px)`;
    };

    const onUp = () => {
      if (!isDragging) return;

      isDragging = false;
      activeHandle = null;

      const value = sliderOption.action.value;
      if (value.lower > value.upper) return;

      let newFilterTree: any = null;

      if (
        sliderOption.status === 'selected' &&
        (!this.filterHelper.isEqual(rangeFilter[option.filterOptionId], sliderOption) ||
          !isFilterVertical) &&
        value.lower !== null &&
        value.upper !== null
      ) {
        if (isFilterVertical) this.domHelper.scrollToTop({ isFilter: true });
        this.persistRangeState(ctx);
        newFilterTree = this.addRangeValueToFilterTree(
          filterState,
          option,
          { key: option.filterOptionId, value: sliderOption.action.value },
          horizontalHasApplyButton
        );
      }

      if (value.lower === min && value.upper === max) {
        sliderOption.status = 'unselected';
        if (isFilterVertical) this.domHelper.scrollToTop({ isFilter: true });
        this.persistRangeState(ctx);
        newFilterTree = this.removeRangeValueFromFilterTree(filterState, {
          key: option.filterOptionId,
          value,
        });
      }

      if (newFilterTree) {
        this.commitFilterTreeChange(newFilterTree, ctx);
      }
    };

    return { onDown, onMove, onUp };
  }

  /**
   * Formats currency symbols for range slider unit elements.
   * This method is public to allow FilterController to delegate currency formatting.
   *
   * @param container - The container element to query for range slider unit elements
   *
   * @remarks
   * This method is called both from renderOptionRangeSlider() and from FilterController
   * to ensure consistent currency formatting across all range sliders.
   */
  formatRangeSliderCurrency(container: HTMLElement): void {
    const _formatMoney = this.filterHelper.formatMoney('');

    // Query unit elements within the container
    const units = container?.querySelectorAll('.boost-sd__filter-option-range-slider-unit');
    if (units && units?.length > 0) {
      units?.forEach((unit) => {
        if (unit) unit.innerHTML = _formatMoney;
      });
    }
  }

  /**
   * Initializes slider option from filter tree state
   */
  protected initSliderOption(
    option: any,
    filterTree: any[],
    inputLower?: HTMLInputElement,
    inputUpper?: HTMLInputElement
  ): any {
    const { min, max } = option.values;
    const sliderOption = {
      action: {
        key: option.filterOptionId,
        value: { lower: min, upper: max },
        range: { lower: min, upper: max },
        display: { lower: -100, upper: 0 },
        label: option.label,
        position: option.position,
        moneyFormatValue: option.moneyFormatValue,
        actionId: 'slider',
      },
      type: 'range',
      status: 'unselected',
      lastActiveHandle: 'lower' as 'lower' | 'upper',
    };

    const rangeFilter = filterTree?.filter((f: any) => f?.data?.key === option.filterOptionId);
    if (rangeFilter?.length > 0) {
      const valueSelected = rangeFilter[0]?.data?.value;

      if (valueSelected?.lower < min) {
        sliderOption.action.value.lower = min;
        if (inputLower) {
          inputLower.value = String(min);
        }
      }

      if (valueSelected?.upper > max) {
        sliderOption.action.value.upper = max;
        if (inputUpper) {
          inputUpper.value = String(max);
        }
      }

      sliderOption.action.value = this.filterHelper.toRangeValue(rangeFilter[0].data.value);
      const range = max - min;

      const newTranLower = 100 - ((sliderOption.action.value.lower - min) / range) * 100;
      const newTranUpper = 100 - ((sliderOption.action.value.upper - min) / range) * 100;

      sliderOption.action.display = {
        lower: Math.min(newTranLower, 100),
        upper: Math.max(newTranUpper, 0),
      };
    }

    return sliderOption;
  }

  /**
   * Adds range value to filter tree
   */
  protected addRangeValueToFilterTree(
    filterTree: any[],
    option: any,
    action: { key: string; value: any },
    horizontalHasApplyButton: boolean
  ): any[] {
    const { key, value } = action;
    const rangeFilter = filterTree?.filter((f: any) => f?.data?.key === key);

    if (rangeFilter?.length > 0) {
      // Ensure existing filter has correct structure
      const correctStructure = this.generateFilterDataForRange(key, value, option);
      rangeFilter.forEach((f: any) => {
        f.data = correctStructure.data;
        f.metaData = correctStructure.metaData;
        f.type = correctStructure.type;
      });
    } else {
      const filter = this.generateFilterDataForRange(key, value, option);
      filterTree.push(filter);
    }

    const { isShortenUrlParam } = this.appService.filterSettings;
    const stringValue = this.filterHelper.toStringValue(value);
    this.filterHelper.setQueryParamHistory(key, stringValue, false, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
    });

    if (horizontalHasApplyButton) {
      const filterOptionItemSelecting =
        this.filterStore.getState('filterOptionItemSelecting') || {};

      if (!filterOptionItemSelecting[`${option.filterOptionId}`]) {
        filterOptionItemSelecting[`${option.filterOptionId}`] = true;
      }

      this.filterStore.setState({ filterOptionItemSelecting });
    }

    return filterTree;
  }

  /**
   * Removes range value from filter tree
   */
  protected removeRangeValueFromFilterTree(
    filterTree: any[],
    option: { key: string; value: any }
  ): any[] {
    const { key, value } = option;
    if (!key || !value) {
      return filterTree;
    }

    const encodeValue = this.filterHelper.toStringValue(value);
    const { isShortenUrlParam } = this.appService.filterSettings;
    this.filterHelper.removeQueryParamHistory(key, encodeValue, {
      isShortenUrlParam,
      urlScheme: this.filterHelper.getUrlScheme(),
      preventPushHistory: false,
    });

    return filterTree.filter((f: any) => f.data.key !== key);
  }
}
